import type { LorePart, LoreMessageWithParts } from "./types";
import { isTextPart, isReasoningPart, isToolPart } from "./types";
import {
  db,
  ensureProject,
  loadForceMinLayer,
  saveForceMinLayer,
  saveSessionTracking,
  loadSessionTracking,
} from "./db";
import {
  type CacheEconomicsResult,
  decideCacheStrategy,
} from "./cache-economics";
import { config } from "./config";
import { formatDistillations } from "./prompt";
import { normalize } from "./markdown";
import { offloadAllOrTimeout, READ_JOB_TIMED_OUT } from "./read-offload";
import type { ReadParam } from "./read-job";
import * as log from "./log";

type MessageWithParts = LoreMessageWithParts;

// Token estimate: ~3 chars per token. Validated against real API data across
// 200+ turn-pairs: chars/3 gives ~1.68x ratio (actual/estimate), best among
// heuristics tested. The gap is overhead (system prompt, tool definitions,
// conversation structure) which calibratedOverhead captures via EMA.
function estimate(text: string): number {
  return Math.ceil(text.length / 3);
}

function estimateParts(parts: LorePart[]): number {
  let total = 0;
  for (const part of parts) {
    if (isTextPart(part)) total += estimate(part.text);
    else if (isReasoningPart(part) && part.text) total += estimate(part.text);
    else if (isToolPart(part) && part.state.status === "completed")
      total += estimate(part.state.output) + estimate(part.tool) + 50;
    else if (isToolPart(part) && part.state.status === "error")
      total += estimate(part.state.error) + estimate(part.tool) + 50;
    else total += 20; // metadata overhead for other part types
  }
  return total;
}

function estimateMessage(msg: MessageWithParts): number {
  return estimateParts(msg.parts) + 20; // role/metadata overhead
}

// Cached model context limit — set by system transform hook, used by message transform
let contextLimit = 200_000; // sensible default
let outputReserved = 32_000;

// ---------------------------------------------------------------------------
// Tier-based context management
//
// Three quality tiers based on empirical model effectiveness:
//   Tier 1: 0 – 200K tokens (best quality, preferred operating range)
//   Tier 2: 200K – 500K tokens (acceptable quality)
//   Tier 3: 500K – model context limit (degraded — quality penalty ramps hard)
//
// Within tier 1, a per-turn economic comparison decides whether to compress
// (bust the cache) or continue growing:
//   bustCost     = compressedSize × cacheWriteCostPerToken
//   continueCost = currentSize   × cacheReadCostPerToken × qualityCostMultiplier
// If bustCost ≥ threshold × continueCost, don't compress — reads are cheap.
//
// Quality cost multiplier: past the tier-1 boundary, models measurably ignore
// the middle of the window (lost-in-the-middle), so each additional token of
// context is worth progressively less. Rather than a hard cliff at a fixed
// ceiling, `qualityCostMultiplier` inflates the perceived "continue" cost as
// the window grows — cheap cache reads on a degraded window are a false
// economy, and the penalty makes that explicit. The multiplier grows without
// bound, so compression is ALWAYS eventually chosen (a 1M-context model is
// pulled back toward its effective range instead of running to the raw limit),
// but a marginally-over session on a very cheap cache may still continue one
// or two turns — no discontinuity. The QUALITY_HARD_CEILING is retained only as
// a safety net on the l0cap-disabled / no-pricing pass-through path, where the
// economic gate never runs.
//
// Rolling bust detection: once SUSTAINED_BUST_THRESHOLD consecutive turns bust
// the cache, stop trying to compress — something structural is causing busts,
// and compression just adds cost on top.
// ---------------------------------------------------------------------------

/** Tier boundary tokens. Configurable for testing. */
const TIER_BOUNDARIES = [200_000, 500_000] as const;

/**
 * Quality hard-ceiling fill fraction: the fraction of the model's context
 * window past which raw layer-0 pass-through is never allowed. Fill-relative
 * (like `qualityCostMultiplier`) so it scales across window sizes: 0.8 is
 * ~160K on a 200K model and ~800K on a 1M model.
 *
 * This is a last-resort SAFETY NET on the pass-through path where the graduated
 * `qualityCostMultiplier` economic gate never runs — l0cap disabled (no pricing
 * / targetCacheRead=0) or a free-write session (ceiling ~0.65×maxInput). It is
 * intentionally set ABOVE the free-write operating fraction (0.65) so it never
 * pre-empts the free-write early-compression path; it only fires deep in the
 * degraded regime. `layer0Passes` enforces it only when it is the binding
 * (tightest) ceiling — i.e. when `layer0Ceiling >= this`.
 *
 * NOTE on scope: because `computeLayer0Cap` now clamps its output to the context
 * window, a cheap-cache model (large cost-derived cap → clamped to the window →
 * `layer0Ceiling ≈ maxInput ≈ 0.97×window ≥ 0.8×window`) DOES let this clause
 * bind. So on the cheap-cache priced path this is a routine gate, not a purely
 * degenerate fallback. That is intentional (it forces compression on a
 * genuinely degraded cheap-cache window at ~0.8 fill, where the graduated
 * multiplier would also compress). It stays inert only when a tighter
 * cost-derived `layer0Ceiling` binds first (expensive-cache / small target$).
 *
 * NOTE: replaces the former fixed 500K constant, which was unreachable below a
 * 500K window and so silently never fired on smaller windows.
 */
const QUALITY_HARD_CEILING_FRACTION = 0.8;

/**
 * Absolute last-resort passthrough margin: never pass through raw at layer 0
 * once the input exceeds this fraction of maxInput (leaves headroom for a
 * mispredicted turn to still fit before the API limit). Module-level so
 * `layer0Passes()` can be shared by transformInner (the actual decision) and
 * isLargeColdStart (the pipeline's turn-1 LTM prediction) — the two MUST NOT
 * diverge (#961 Seer 15319715).
 */
const HARD_CEILING_MARGIN = 0.95;

/**
 * Shared layer-0 passthrough predicate: returns true when a session of size
 * `layer0Input` may pass through UNCOMPRESSED at layer 0. Compression is forced
 * when this returns false. Used by BOTH transformInner (the layer decision) and
 * isLargeColdStart (the turn-1 LTM prediction) so their logic can never drift.
 *
 * Three ceilings must hold: the cost/API layer-0 ceiling, the absolute maxInput
 * margin, and a fill-relative quality hard ceiling.
 *
 * The quality hard ceiling is applied ONLY when it is the binding (tightest)
 * ceiling — i.e. `layer0Ceiling >= QUALITY_HARD_CEILING_FRACTION × window`.
 * It stays inert when a tighter cost-derived `layer0Ceiling` binds first
 * (expensive-cache / small target$), exactly as the former fixed 500K constant
 * was inert whenever a tighter l0cap was set. It DOES bind on the cheap-cache
 * priced path, where `computeLayer0Cap` clamps the (huge) cost cap to the window
 * so `layer0Ceiling ≈ maxInput ≥ the quality ceiling` — see the scope note on
 * `QUALITY_HARD_CEILING_FRACTION`. Making it fill-relative extends the net to
 * smaller windows without perturbing the free-write operating band (0.65).
 */
function layer0Passes(
  layer0Input: number,
  layer0Ceiling: number,
  maxInput: number,
): boolean {
  const window = contextLimit > 0 ? contextLimit : 200_000;
  const qualityHardCeiling = window * QUALITY_HARD_CEILING_FRACTION;
  // Only enforce the quality hard ceiling when it is the binding constraint,
  // i.e. the cost/free-write ceiling is looser than it. This preserves the
  // historical "inert on the priced path" behavior (fixed 500K was unreachable
  // whenever a tighter l0cap or free-write ceiling was set).
  const qualityCeilingBinds = layer0Ceiling >= qualityHardCeiling;
  return (
    layer0Input <= layer0Ceiling &&
    (!qualityCeilingBinds || layer0Input <= qualityHardCeiling) &&
    layer0Input <= maxInput * HARD_CEILING_MARGIN
  );
}

/**
 * Quality penalty slope for the fill-fraction ramp. Past the per-model quality
 * knee, the perceived "continue" cost is inflated by `1 + SLOPE * excess^2`,
 * where `excess ∈ [0,1]` is how far the fill fraction has travelled from the
 * knee toward a full window (see `qualityCostMultiplier`). At the knee the
 * multiplier is 1 (no penalty); at a completely full window it is `1 + SLOPE`.
 *
 * The gate compresses when `bustCost < threshold × continueCost`. For a ~30%
 * compression ratio that crossover is reached at a multiplier of roughly the
 * model's write/read ratio × 0.3 / 0.85 — about 2–4.4× for our focus models
 * (DeepSeek-via-OpenRouter, MiniMax-M3, Gemini, Anthropic). SLOPE=20 places
 * that crossover around 55–65% fill, i.e. inside the tier-2 "acceptable" band,
 * so compression fires well before the window is degraded while leaving cheap
 * models a little more headroom than expensive ones (which cross earlier).
 *
 * This is fill-relative, so it scales across window sizes automatically: 60%
 * fill is ~120K on a 200K model and ~600K on a 1M model — both the empirically
 * good operating range for that model.
 */
const QUALITY_PENALTY_SLOPE = 20;

/**
 * Quality penalty curve exponent. >1 makes the ramp convex: quality holds flat
 * until the knee, then degrades with an accelerating curve — matching the
 * empirical "holds, then collapses" shape of lost-in-the-middle degradation
 * (as opposed to a concave log curve, which would drop sharply then flatten).
 */
const QUALITY_PENALTY_EXPONENT = 2;

/**
 * Default quality knee as a fraction of the model's context window, used when
 * no per-model knee has been measured. Below this fill the window is treated as
 * pristine (no penalty); above it the penalty ramps.
 *
 * Grounded in the long-context degradation literature — "Lost in the Middle"
 * (Liu et al., TACL 2023, arXiv:2307.03172) and Chroma's "Context Rot" (Hong et
 * al., 2025, 18 models incl. Claude 4 / GPT-4.1 / Gemini 2.5) — which show
 * degradation is continuous, model-specific, and materially present well before
 * a window is full, but publish no universal knee constant. 0.4 (40% fill) is a
 * conservative single default; per-model knees are expected to be measured
 * empirically (via the eval harness) and supplied through `setQualityKnee`.
 */
export const DEFAULT_QUALITY_KNEE_FRACTION = 0.4;

/**
 * Per-model quality knee as a fraction of the context window. Set by the host
 * adapter from models.dev data (via `setQualityKnee`); falls back to
 * DEFAULT_QUALITY_KNEE_FRACTION when unset or out of range.
 */
let qualityKneeFraction = DEFAULT_QUALITY_KNEE_FRACTION;

/**
 * Set the per-model quality knee (fill fraction where degradation begins).
 * Clamped to (0, 1); undefined, out-of-range, or non-finite values reset to
 * the literature-grounded default (DEFAULT_QUALITY_KNEE_FRACTION).
 */
export function setQualityKnee(fraction: number | undefined): void {
  qualityKneeFraction =
    fraction != null &&
    Number.isFinite(fraction) &&
    fraction > 0 &&
    fraction < 1
      ? fraction
      : DEFAULT_QUALITY_KNEE_FRACTION;
}

/** Returns the current per-model quality knee fraction (for tests). */
export function getQualityKnee(): number {
  return qualityKneeFraction;
}

/**
 * Literature-seeded per-model-family quality knees (fill fraction where
 * lost-in-the-middle degradation becomes material). They are conservative priors
 * read off the public long-context degradation literature so per-model
 * compression is at least directionally right instead of a flat 0.4 for everyone:
 *
 *   - "Lost in the Middle" (Liu et al., TACL 2023, arXiv:2307.03172)
 *   - Chroma "Context Rot" (Hong et al., 2025, 18 models incl. Claude 4 /
 *     GPT-4.1 / Gemini 2.5) — degradation is continuous, model-specific, and
 *     begins well before a window is full; frontier models hold quality longer.
 *
 * We ATTEMPTED to replace these with values measured on our own eval (#1404-B,
 * using #1402's behavioral rot A/B over the MATRIX-V5 single-long cells:
 * deepseek/m3 @ cap200, sonnet @ cap200+cap500, both arms, N=5). The result was
 * a documented NEGATIVE: the 5 contextrot behavioral signals (tool_error,
 * edit_failure, retry, reread, self_correction) fired only ~24 times across
 * ~1,958 steps (1.2% density), so NO scenario reached a fittable / Wilson-
 * significant degradation knee (every arm returned knee=null,
 * ratioSignificant=false). Clean, structured coding-agent transcripts simply do
 * not generate enough behavioral degradation to trace a per-model rot curve —
 * consistent with METHODOLOGY.md's caveat that contextrot needs ~150+
 * degradation-bearing steps. Per the gating rule (adopt a measured knee only
 * when Wilson-significant with adequate n; otherwise keep the literature prior),
 * ALL priors below are retained unchanged. Raw analysis:
 * quality/eval-artifacts/rot-behavioral-matrix-v5.json. Revisit only if a
 * higher-signal (longer, more error-prone) eval produces a measurable knee.
 *
 * Keyed by a lowercased family stem matched as a PREFIX of the bare model id
 * (the last `/`-segment). LONGEST matching prefix wins, so entries may be listed
 * in any order — a broad stem (`claude-`) and a more specific one
 * (`claude-opus`) can coexist and the specific one is chosen. Frontier families
 * get a higher knee (degrade later); cheaper/smaller models a lower one.
 * Anything unmatched falls back to the default. Deliberately listed
 * broad-before-specific so the longest-prefix logic is load-bearing (a plain
 * first-match would return the broad entry).
 */
const QUALITY_KNEE_BY_FAMILY: ReadonlyArray<readonly [string, number]> = [
  // Broad family stems (older / non-frontier members degrade earlier).
  ["claude-", 0.45],
  ["gpt-", 0.45],
  ["gemini-", 0.45],
  // Frontier long-context members — hold quality furthest into the window.
  ["claude-opus", 0.5],
  ["claude-sonnet", 0.5],
  ["gpt-5", 0.5],
  ["gemini-2.5", 0.5],
  ["gemini-3", 0.5],
  // Cheaper / smaller "everyday" models — degrade earlier under context load.
  ["deepseek", 0.35],
  ["minimax", 0.35],
  ["qwen", 0.35],
  ["mistral", 0.35],
  ["llama", 0.35],
];

/**
 * Resolve the quality knee for a model: an explicit config override wins;
 * otherwise the literature-seeded family table (longest prefix match); otherwise
 * the literature-grounded default. The override and table entries are validated
 * the same way `setQualityKnee` validates — out-of-range / non-finite falls
 * through to the next source. Pure; safe to call on the hot path.
 *
 * NOTE: the returned value is a literature PRIOR, not a measured knee. #1404-B
 * attempted to replace the table with values measured via #1402's behavioral rot
 * A/B but found no statistically-significant per-model knee (signal too sparse on
 * coding-agent transcripts — see the QUALITY_KNEE_BY_FAMILY comment above), so
 * the priors stand. Keeping the seed table here (core, next to the default and
 * the multiplier that consumes it) keeps the prior testable without the gateway.
 */
export function resolveQualityKnee(modelID: string, override?: number): number {
  const valid = (v: number | undefined): v is number =>
    v != null && Number.isFinite(v) && v > 0 && v < 1;
  if (valid(override)) return override;
  // req.model is often provider-qualified (e.g. "anthropic/claude-opus-4.8",
  // "openrouter/deepseek/deepseek-v4-flash"). Match the family stem against the
  // LAST path segment (the bare model id) so a leading provider/aggregator
  // prefix never hides the family.
  const bare = modelID.toLowerCase().split("/").pop() ?? modelID.toLowerCase();
  let best: number | null = null;
  let bestLen = -1;
  for (const [stem, knee] of QUALITY_KNEE_BY_FAMILY) {
    if (bare.startsWith(stem) && stem.length > bestLen) {
      best = knee;
      bestLen = stem.length;
    }
  }
  return best ?? DEFAULT_QUALITY_KNEE_FRACTION;
}

/**
 * Continuous quality penalty applied to the "continue" cost in shouldCompress.
 *
 * Quality degradation ("lost in the middle") is a function of how FULL the
 * context is relative to the model's window, not an absolute token count — a
 * 200K session is pristine in a 1M-window model but degraded in a 200K model.
 * So the penalty keys on the fill fraction `f = tokens / contextLimit`:
 *
 *   f ≤ knee            → 1 (pristine regime, no penalty)
 *   f > knee            → 1 + SLOPE × excess^EXP,
 *                         excess = (f - knee) / (1 - knee)  ∈ (0, 1]
 *
 * Monotonic and bounded by `1 + SLOPE` at a full window; because the crossover
 * for realistic write/read ratios is well below the full window, compression is
 * always eventually chosen as the window fills — regardless of how cheap the
 * model's cache reads are (which is what made the old cost-only cap explode to
 * tens of millions of tokens for cheap models and never compress).
 */
export function qualityCostMultiplier(tokens: number): number {
  const window = contextLimit > 0 ? contextLimit : 200_000;
  const knee = qualityKneeFraction;
  const fill = tokens / window;
  if (fill <= knee) return 1;
  const excess = (fill - knee) / (1 - knee);
  return 1 + QUALITY_PENALTY_SLOPE * excess ** QUALITY_PENALTY_EXPONENT;
}

/** Cache pricing per token (USD). Set by host adapter via setCachePricing(). */
let cacheWriteCostPerToken = 0;
let cacheReadCostPerToken = 0;

/**
 * Set cache pricing for the current model. Called by the host adapter after
 * looking up model cost data. Required for tier-based bust-vs-continue
 * decisions. When not set (both 0), tier decisions fall back to conservative
 * defaults: do NOT compress (preserve the cache).
 */
export function setCachePricing(writeCost: number, readCost: number) {
  cacheWriteCostPerToken = Math.max(0, writeCost);
  cacheReadCostPerToken = Math.max(0, readCost);
}

/** Returns current pricing (for tests). */
export function getCachePricing(): { write: number; read: number } {
  return { write: cacheWriteCostPerToken, read: cacheReadCostPerToken };
}

// Cost-aware layer-0 token cap. When > 0, the layer-0 passthrough gate uses
// min(maxInput, maxLayer0Tokens) instead of maxInput alone. Derived from the
// model's cache-read cost: cap = targetCostPerTurn / costPerToken. This prevents
// expensive models from sending huge contexts at layer 0, where cache-read costs
// compound linearly across turns. Set to 0 to disable (use full context).
let maxLayer0Tokens = 0;

const MIN_LAYER0_FLOOR = 40_000;

/** Quantization step for the LTM token budget (getLtmBudget). The budget is
 *  derived from `usable`, which wobbles every turn via the per-turn overhead
 *  EMA; snapping it to this step keeps the budget — and therefore the
 *  ltm.forSession() greedy entry-packing boundary — stable across normal
 *  wobble, so the selected entry set (and thus the pinned system[2] block) only
 *  changes when knowledge genuinely changes. Without this, the lowest-ranked
 *  pinned entry drops/re-enters as the budget drifts, churning the set and
 *  appending a durable prompt-delta into the cached prefix every turn (a cache
 *  bust). Sized to absorb typical per-turn overhead drift on high-context
 *  models. */
const LTM_BUDGET_STEP = 8_000;

/** Hard ceiling on a single LTM budget as a fraction of `usable`, so the
 *  quantization floor below (LTM_BUDGET_STEP) cannot turn a small requested
 *  fraction into a large share of a small/short context window. Chosen well
 *  above the default ltm (0.05) and preference (0.02) fractions, so on large
 *  models it never binds and the budget is exactly the historical value; it
 *  only clips the 8K floor on windows with less than ~40K usable tokens. */
const MAX_LTM_BUDGET_FRACTION = 0.2;

/** Tighter ceiling for short-lived sub-agent sessions (1-3 turns). Sub-agents
 *  run a focused task and should keep their window for real work rather than
 *  injected knowledge. */
const SUBAGENT_MAX_LTM_BUDGET_FRACTION = 0.1;

/** Consecutive zero-cache-write turns before treating the session as free-write. */
const NO_CACHE_WRITE_THRESHOLD = 3;

/** EMA smoothing for the per-session prefix-churn rate (fraction of recent real
 *  turns that were `prefix-rewrite` busts). ~3-turn effective window. */
export const PREFIX_CHURN_ALPHA = 0.35;

/** Prefix-churn rate at/above which the cache warmer suppresses warming. While
 *  the distilled prefix is being rewritten this often, every warmup lands as a
 *  partial (it refreshes a prefix the next real turn replaces) — pure waste.
 *  Warming re-arms automatically once real turns stop rewriting the prefix and
 *  the EMA decays below this threshold.
 *
 *  🔴 Set STRICTLY ABOVE PREFIX_CHURN_ALPHA so a SINGLE one-off rewrite (which
 *  bumps the from-zero EMA to exactly ALPHA) does NOT block — that warmup would
 *  land as a clean refresh because the prefix is now stable again. Only SUSTAINED
 *  churn trips it: two consecutive rewrites give EMA ≈ 0.58, and a rewrite every
 *  ≤2 turns holds the EMA above this threshold. */
export const PREFIX_CHURN_WARM_BLOCK = 0.4;

/** Layer-0 ceiling fraction for free-write sessions.
 *  65% of maxInput ≈ 109k for 200k context → ~59k headroom for tool-heavy turns. */
const FREE_WRITE_LAYER0_FRACTION = 0.65;

/**
 * On uncalibrated turns, multiply the chars/3 estimate to approximate the real
 * token count. chars/3 undercounts by ~1.68x on real data, but the overhead EMA
 * captures most of the gap; 1.5 provides a safe margin. Module-scoped so the
 * layer-0 sizing helper (and isLargeColdStart) share one definition with the
 * transform path.
 */
export const UNCALIBRATED_SAFETY = 1.5;

/**
 * Empirical ratio of REAL provider tokens to the gradient's chars/3 body
 * estimate (validated ~1.63–1.68 across 200+ turn-pairs). Used to keep the
 * budget math on a single, consistent scale (issue: distilled-prefix churn from
 * usable-collapse):
 *
 *  - calibrate(): overhead = actualInput(REAL) − messageEstimate×RATIO − ltm, so
 *    `overhead` is TRUE system+tools and does NOT silently absorb the body
 *    undercount. Previously overhead absorbed ~0.40·body_real, which for large
 *    growing sessions inflated overhead to ~125K on a 200K model, collapsed
 *    `usable` to ~25K, and forced the session to Layer 4 where the distilled
 *    prefix is re-rendered every turn (the messages[1] cache bust).
 *  - body budgets: a body of B_real real tokens has a chars/3 estimate
 *    B_real/RATIO, so a REAL-token budget is converted to the chars/3 scale the
 *    body estimates are measured in by dividing by RATIO (so the body occupies
 *    at most `fraction × usable` REAL tokens).
 *  - fitsWithSafetyMargin(): the acceptance gate inflates the chars/3 body back
 *    to real and adds overhead+ltm before comparing to maxInput.
 */
export const BODY_TOKEN_RATIO = 1.68;

/**
 * True when the session's upstream has reported zero cache_creation_input_tokens
 * for at least {@link NO_CACHE_WRITE_THRESHOLD} consecutive turns.  Covers both
 * free-write-cache providers (e.g. MiniMax passive caching) and non-caching
 * providers.  Self-correcting: any turn with non-zero cache writes resets
 * the counter.
 *
 * Note: a fully cache-warmed Anthropic session could theoretically trigger this
 * if the prompt stays byte-identical for 3+ turns, but that's rare in practice
 * (LTM injection and new messages almost always cause cache writes).
 *
 * Uses Map.get() to avoid creating phantom SessionState entries — same pattern
 * as {@link getConsecutiveBusts}.
 */
export function isFreeWriteSession(sessionID: string): boolean {
  return (
    (sessionStates.get(sessionID)?.zeroCacheWriteTurns ?? 0) >=
    NO_CACHE_WRITE_THRESHOLD
  );
}

/** Consecutive-bust count at/above which a session is in a "sustained-bust
 *  regime": the prompt cache is being rewritten nearly every turn, so the
 *  "continue" path is paying cache *write* cost — not the cheap read cost the
 *  tier gate normally assumes.
 *
 *  Kept low (2): at large context windows a single full cache bust rewrites
 *  hundreds of thousands of tokens at ~12.5× the read price, so even two
 *  consecutive full busts is already too expensive to keep paying. A low
 *  threshold makes shouldCompress() reprice "continue" at the write rate
 *  sooner (compressing earlier instead of letting the context grow). */
const SUSTAINED_BUST_THRESHOLD = 2;

/** Number of a newly-tracked session's first in-process transforms during which
 *  bust spirals are treated as expected cold-start rewrites, not alerts (#797).
 *
 *  When Lore first tracks a large conversation in-process the opening turns
 *  inherently rewrite the body — turn-1 cold write, turn-2 LTM injection,
 *  turn-3 layer 0→1 transition — and each counts as a consecutive cache bust.
 *  The first cold-start episode within this window emits a low-severity Sentry
 *  breadcrumb (informational); past the window, sustained busts fire a
 *  high-severity Sentry alert (one per spiral episode, debounced, cleared on
 *  recovery). The economic `consecutiveBusts` counter (which drives
 *  compression) is left untouched. Kept at 3 to span the documented
 *  cold-start sequence. Like `consecutiveBusts`, the per-session
 *  `transformCount` that drives this is NOT restored from the DB — a resumed/
 *  restarted session re-enters the grace, which is harmless (it is already
 *  calibrated and does not cold-start bust). */
export const COLD_START_GRACE_TURNS = 3;

/** Categorical cause of a cache bust. Defined in core (rather than gateway) so
 *  `recordCacheUsage` can discriminate non-user-driven busts (e.g. meta-
 *  distillation prefix rewrites) from genuine user-context growth without
 *  creating a circular gateway→core import. Gateway's own categorization
 *  (`categorizeBust` in `cache-analytics.ts`) assigns one of these values and
 *  threads it into `recordCacheUsage`. */
export type CacheBustCause =
  | "first-turn" // session's first request (unavoidable)
  | "system-host-change" // divergence in system[0]: agent-owned host prompt
  | "system-ltm-change" // divergence in system[1]/[2]: lore's own LTM blocks
  | "tools-change" // tool definitions changed
  | "prefix-rewrite" // distilled prefix content changed (meta-distillation)
  | "window-shift" // raw window eviction changed message positions
  | "idle-resume" // first turn after idle detection (cold cache)
  | "incremental" // normal append (cache hit, write only new tail)
  | "unknown"; // unclassified

/**
 * Decide whether compression is economical at a tier boundary.
 *
 * The core comparison is bustCost (write the compressed context once) vs
 * continueCost (keep sending the full context each turn). Normally "continue"
 * is a cheap cache *read*, so growing the context is fine. But when the session
 * is in a sustained-bust regime (`consecutiveBusts >= SUSTAINED_BUST_THRESHOLD`),
 * the full context is being *written* every turn anyway — so "continue" costs
 * write, not read. In that regime we price continueCost at the write rate,
 * which makes compression economical precisely when it's needed (growth-driven
 * busts), instead of refusing to compress and letting the context grow
 * unbounded.
 *
 * @param currentTokens    - expected input tokens if we stay at the current layer
 * @param compressedTokens - expected tokens after compression
 * @param consecutiveBusts - how many turns in a row we've busted the cache
 * @param opts.threshold   - bust cost must be < threshold × continue cost (default 0.85)
 * @param opts.freeWrite   - when true, compression is free (no cache write cost)
 * @returns true if compression is worth it
 */
export function shouldCompress(
  currentTokens: number,
  compressedTokens: number,
  consecutiveBusts: number,
  opts?: { threshold?: number; freeWrite?: boolean },
): boolean {
  const { threshold = 0.85, freeWrite = false } = opts ?? {};

  // Free-write cache (or no cache): compression never incurs a write cost.
  // Always compress at tier boundaries — there's no economic downside — EXCEPT
  // after a sustained run of busts, where repeated free compression that keeps
  // overflowing is just churn (compress→overflow loop). This guard applies to
  // free-write sessions only; paid-cache sessions are handled by the
  // write-cost repricing below (Fix C).
  if (freeWrite) {
    return consecutiveBusts < SUSTAINED_BUST_THRESHOLD;
  }

  // If no pricing data at all, fall back to conservative: do NOT compress.
  // (Models with cache_read but no cache_write still reach the priced path
  // below, because getModelSpec derives cache_write from input cost — so this
  // branch only fires when models.dev has NO cost entry whatsoever. The
  // fill-based qualityCostMultiplier on the priced path is what fixes the
  // cheap-cache "never compress" bug; see computeLayer0Cap + qualityCostMultiplier.)
  if (cacheWriteCostPerToken <= 0 || cacheReadCostPerToken <= 0) return false;

  const sustainedBust = consecutiveBusts >= SUSTAINED_BUST_THRESHOLD;

  const bustCost = compressedTokens * cacheWriteCostPerToken;
  // In a sustained-bust regime the "continue" path is paying cache *write*
  // cost on the whole context every turn, not the cheap read cost. Price it
  // accordingly so the gate reflects reality. Otherwise use the read rate.
  const continuePerToken = sustainedBust
    ? cacheWriteCostPerToken
    : cacheReadCostPerToken;
  // Quality penalty: past the best-quality tier, each retained token is worth
  // progressively less (lost-in-the-middle), so inflate the perceived continue
  // cost by qualityCostMultiplier. This makes compression win earlier and
  // earlier as the window grows — a graduated ramp rather than a hard ceiling —
  // and, because the multiplier is unbounded, guarantees compression is
  // eventually chosen instead of re-reading a degraded window forever. (#961)
  const continueCost =
    currentTokens * continuePerToken * qualityCostMultiplier(currentTokens);

  // Compress only if the bust cost is meaningfully less than continuing.
  // Note: we deliberately do NOT short-circuit to "never compress" on a high
  // bust count anymore. Structural system[2] busts (the historical cause of
  // runaway bust counts) are eliminated by the reorder-tolerant LTM pin, so a
  // sustained bust now indicates raw-window growth — exactly the case where
  // compressing (writing once) is cheaper than continuing to rewrite the whole
  // grown context every turn.
  return bustCost < threshold * continueCost;
}

/**
 * Determine which tier the given token count falls into.
 * Returns 0, 1, or 2 corresponding to the tier index.
 */
export function getTier(tokens: number): number {
  if (tokens <= TIER_BOUNDARIES[0]) return 0;
  if (tokens <= TIER_BOUNDARIES[1]) return 1;
  return 2;
}

/**
 * Record cache usage from an API response. Tracks consecutive busts for
 * the rolling bust detection used by shouldCompress().
 *
 * A "bust" is when cache_write > 50% of total input tokens.
 *
 * @param cacheWrite  - cache_creation_input_tokens from the API response
 * @param cacheRead   - cache_read_input_tokens from the API response
 * @param inputTokens - input_tokens from the API response (uncached portion only —
 *                      Anthropic's input_tokens excludes both cache reads and writes)
 * @param sessionID   - session that produced this response
 * @param isIdleResume  - true when the upstream turn is the first one after an
 *                        idle gap past the cache TTL; re-warms are expected
 *                        cold-cache writes, not sustained growth
 * @param bustCause     - categorical cause of the bust (when the gateway has
 *                        computed it). `prefix-rewrite` busts are exempted from
 *                        the counter because they are self-inflicted by Lore's
 *                        own meta-distillation — they don't reflect user-context
 *                        growth. `undefined` falls through to the legacy
 *                        counter behavior (treat as a user-driven bust).
 */
export function recordCacheUsage(
  cacheWrite: number,
  cacheRead: number,
  inputTokens: number,
  sessionID?: string,
  isIdleResume = false,
  bustCause?: CacheBustCause,
): void {
  if (!sessionID) return;
  const state = getSessionState(sessionID);

  // Total = cacheWrite + cacheRead + uncached input. Anthropic's input_tokens
  // field is only the uncached portion, NOT the total — using it alone as the
  // denominator makes every cached turn look like a bust (e.g. 1000/3 >> 0.5).
  const total = cacheWrite + cacheRead + inputTokens;
  if (total > 0) {
    const bustRatio = cacheWrite / total;
    const prev = state.consecutiveBusts;
    if (bustRatio > 0.5) {
      // Two classes of bust are NOT counted toward consecutiveBusts:
      //
      // (a) Idle-resume re-warms: expected cold-cache writes when the user
      //     pauses longer than the conversation cache TTL. Counting these
      //     produced false bust-spiral alerts on bursty sessions whose
      //     turns are spaced beyond the TTL — every threshold-crossing bust
      //     in the ses_14b9bf3d… incident followed an 11m–2h idle gap past
      //     the 5m TTL. HOLD the counter on an idle resume: neither advance
      //     toward the threshold nor erase a genuine prior run (a real
      //     warm-window bust that preceded the idle is still real). A
      //     genuine cache HIT (ratio <= 0.5) below still resets, even on an
      //     idle resume.
      //
      // (b) Lore-internal prefix rewrites (`prefix-rewrite`): meta-
      //     distillation consolidates gen-0 segments and rewrites the
      //     synthetic distilled prefix (messages[0/1]) on the next turn.
      //     That's a real prompt-cache bust, but the cause is Lore's own
      //     distillation pipeline — it does NOT reflect user-context growth.
      //     Counting it produced the false bust-spiral alert on opencode
      //     session ses_10f932a26ffeFBKO9ZsjycCCna (Lore session
      //     0XrHNdlsWwgVkX4MH, 2026-06-22): context was bounded at ~130K
      //     tokens (well under the 200K layer-0 cap), but meta-distillation
      //     firing under bust pressure rewrote the prefix every few turns,
      //     accumulating 2-3 consecutive busts and triggering the alert.
      //     Adding the bust-cause discriminator (this parameter) lets the
      //     counter recognize these self-inflicted busts the same way it
      //     recognizes idle-resume ones.
      const isNonUserBust = isIdleResume || bustCause === "prefix-rewrite";
      if (!isNonUserBust) state.consecutiveBusts++;
    } else {
      state.consecutiveBusts = 0;
    }
    if (state.consecutiveBusts !== prev) {
      log.info(
        `bust-tracker: session=${sessionID.slice(0, 16)} ratio=${bustRatio.toFixed(3)}` +
          ` (write=${cacheWrite} read=${cacheRead} uncached=${inputTokens})` +
          ` busts=${prev}→${state.consecutiveBusts}` +
          (bustCause ? ` cause=${bustCause}` : ""),
      );
    } else if (isIdleResume && bustRatio > 0.5) {
      log.info(
        `bust-tracker: session=${sessionID.slice(0, 16)} idle-resume re-warm` +
          ` ratio=${bustRatio.toFixed(3)} (write=${cacheWrite} read=${cacheRead}` +
          ` uncached=${inputTokens}) — not counted (busts held at ${state.consecutiveBusts})`,
      );
    } else if (bustCause === "prefix-rewrite" && bustRatio > 0.5) {
      log.info(
        `bust-tracker: session=${sessionID.slice(0, 16)} prefix-rewrite (meta-distill)` +
          ` ratio=${bustRatio.toFixed(3)} (write=${cacheWrite} read=${cacheRead}` +
          ` uncached=${inputTokens}) — not counted (busts held at ${state.consecutiveBusts})`,
      );
    }

    // Prefix-churn EMA: a FREE, per-real-turn signal of how often the distilled
    // prefix (messages[0/1]) is being rewritten by meta-distillation. Updated on
    // every real cache event (total>0): 1 when this turn was a prefix-rewrite
    // bust, 0 otherwise — so it decays on clean/incremental turns. The cache
    // warmer reads it (getPrefixChurnRate) to suppress warming while the prefix
    // is actively churning: warming a prefix that the next real turn will rewrite
    // is pure waste (the warmup refreshes a prefix that is about to be replaced).
    // A one-off rewrite (e.g. a single compaction) bumps the EMA once and decays;
    // only SUSTAINED churn (rewrites every few turns) holds it above the block
    // threshold. `idle-resume` busts are NOT churn (indicator 0) — a cold-cache
    // re-warm is unrelated to prefix rewriting.
    const prefixRewriteIndicator = bustCause === "prefix-rewrite" ? 1 : 0;
    state.prefixChurnEma =
      PREFIX_CHURN_ALPHA * prefixRewriteIndicator +
      (1 - PREFIX_CHURN_ALPHA) * state.prefixChurnEma;

    // Free-write detection: track consecutive turns with zero cache writes.
    // Covers providers with free passive caching (e.g. MiniMax) and
    // non-caching providers. Both produce the same observable signal.
    if (cacheWrite === 0) {
      state.zeroCacheWriteTurns++;
      // Crossing the threshold: reset bust counter — prior busts were
      // computed under false pricing assumptions (inflated write cost).
      if (state.zeroCacheWriteTurns === NO_CACHE_WRITE_THRESHOLD) {
        state.consecutiveBusts = 0;
        log.info(
          `free-write-detect: session=${sessionID.slice(0, 16)} ` +
            `${NO_CACHE_WRITE_THRESHOLD} consecutive turns with zero cache writes — ` +
            `treating compression as free`,
        );
      }
    } else {
      state.zeroCacheWriteTurns = 0;
    }
  }
}

// Conservative overhead reserve for first-turn (before calibration):
// accounts for provider system prompt + AGENTS.md + tool definitions + env info
const FIRST_TURN_OVERHEAD = 15_000;

// Calibrated overhead: actual tokens used minus our message estimate (and, since
// Bug 1 lever 3, minus injected LTM — overhead represents system prompt + tools
// only; LTM is subtracted separately at the `usable` computation).
// Null = not yet calibrated (first turn). Updated after every assistant response.
//
// BUG 1 (lever 2): this global is no longer authoritative. Per-session overhead
// lives in SessionState.overheadEma (host system prompt is stable WITHIN a
// session but varies wildly BETWEEN sessions; blending heterogeneous concurrent
// sessions produced a ~20x usable swing that front-busted the distilled prefix).
// The global is now only the first-turn seed / no-session fallback used by
// getOverhead() when no sessionID is provided. Production callers always pass
// sessionID.
let calibratedOverhead: number | null = null;

// ---------------------------------------------------------------------------
// Per-session state
//
// All calibration, layer-tracking, and window-ID state is scoped per session
// using an in-memory Map. This prevents worker sessions (lore-distill,
// lore-curator) from corrupting the main session's sticky-layer guard and
// delta-estimation state when their transform() calls return layer 0.
//
// forceMinLayer is the one field that MUST survive process restarts: when the
// API returns "prompt is too long", the error handler sets forceMinLayer=2.
// If OpenCode restarts before the next turn, the escalation is lost and the
// overflow repeats. forceMinLayer is persisted to SQLite (session_state table)
// and loaded on first access. All other state rebuilds from the first API
// response via UNCALIBRATED_SAFETY.
// ---------------------------------------------------------------------------

type DistillationSnapshot = {
  /** Cached distillation rows from the most recent DB read */
  rows: Distillation[];
  /** ID of the last user message when this snapshot was taken */
  lastUserMsgId: string | null;
};

type SessionState = {
  /** Exact input token count from the last successful API response */
  lastKnownInput: number;
  /** LTM tokens that were in-flight when lastKnownInput was recorded */
  lastKnownLtm: number;
  /** Total messages sent to the model in the last turn (compressed count on layers 1-4) */
  lastKnownMessageCount: number;
  /** Number of messages in the most recent transform() output */
  lastTransformedCount: number;
  /** Layer used by the most recent transform() call — sticky-layer guard */
  lastLayer: SafetyLayer;
  /**
   * High-water compression layer (1-3) this session has ever sustained. Once a
   * session compresses to layer L, decompressing to a LOWER layer re-renders the
   * distilled prefix at messages[0/1] (different budget/strip level) and
   * front-busts the whole prompt cache — then the next turn re-compresses and
   * busts again (0<->N / 2<->1 thrash). Reclaiming window by decompressing is a
   * false economy: the reclaim itself is a full cache write. So the layer is a
   * strict monotonic RATCHET — `effectiveMinLayer` is floored at this every turn,
   * unconditionally (no release on a genuine host-side context shrink).
   *
   * Layer 4 (emergency overflow) is EXCLUDED: it is a deliberate one-shot bust to
   * recover from "prompt too long", not a sustained level, and must never be
   * latched here (that would trap the session at emergency permanently). Only
   * layers 1-3 ratchet.
   */
  maxSustainedLayer: SafetyLayer;
  /** Message IDs in the most recent transform() output — ID-based delta estimation */
  lastWindowMessageIDs: Set<string>;
  /** One-shot force escalation: skip layers below this on the next transform() */
  forceMinLayer: SafetyLayer;
  /** Token estimate from the most recent transform() output (compressed window) */
  lastTransformEstimate: number;
  /** LTM tokens injected for this session's current turn (per-session isolation) */
  ltmTokens: number;
  /**
   * Per-session overhead EMA (system prompt + tools, in real tokens), or null
   * until the first calibrate() with a transform baseline. The dominant term is
   * the host system prompt, which is stable WITHIN a session but varies wildly
   * BETWEEN sessions (different agents/projects). A single module-global EMA
   * blended all concurrent sessions together, producing a ~20x turn-to-turn
   * `usable` swing that front-busted the distilled prefix (Bug 1, session 1GYu).
   * getOverhead(sessionID) prefers this; the global `calibratedOverhead` remains
   * only as the first-turn seed / no-session fallback.
   */
  overheadEma: number | null;
  /**
   * High-water mark for the layer-1 distilled budget (Bug 1, lever 1). Once the
   * distilled prefix has been admitted at a given `distilledBudget`, residual
   * per-turn overhead-EMA wobble must not re-trim it (a front-bust). The trim
   * gate uses max(stageDistBudget, this); this only ratchets UP. Mirrors the
   * raw-window pin's high-water hysteresis. Reset alongside rawWindowCache.
   */
  distilledBudgetHighWater: number;
  /** Distilled prefix cache (Approach C) */
  prefixCache: PrefixCache | null;
  /** Raw window pin cache (Approach B) */
  rawWindowCache: RawWindowCache | null;
  /**
   * Wall-clock timestamp (epoch ms) of the most recent transform() call for this
   * session. Used by onIdleResume() to detect cold-cache resumption — when the
   * gap between turns exceeds Anthropic's prompt cache eviction window (5 min
   * default / 1 hour extended), the byte-identity caching subsystems
   * (prefixCache, rawWindowCache) are providing no value because the cache is
   * already cold. Refreshing them on resume lets us produce a better-fitting
   * window without paying a cache cost we'd otherwise be trying to preserve.
   * 0 = never set (first turn).
   */
  lastTurnAt: number;
  /**
   * Set true by onIdleResume() when an idle-resume reset just fired; consumed
   * (and cleared) by the LTM degraded-recovery branch in the OpenCode hook to
   * skip the conversation-vs-LTM token comparison. After idle eviction the
   * cache-bust cost is effectively zero, so we should always recover LTM on
   * the post-idle turn regardless of conversation size.
   */
  cameOutOfIdle: boolean;
  /**
   * Set true by onIdleResume() alongside cameOutOfIdle; consumed (and cleared)
   * by transformInner() to activate the post-idle compact layer. When true AND
   * distillations exist, transform skips layer 0 (full-raw passthrough) and
   * uses a tighter raw budget for layer 1. Rationale: on a cold cache the
   * entire context is a cache WRITE — a smaller total means lower write cost,
   * and aggressive idle distillation already captured the older history.
   */
  postIdleCompact: boolean;
  /** Consecutive turns at layer >= 2. When >= 3, log a compaction hint. */
  consecutiveHighLayer: number;
  /** Consecutive turns where the cache was busted (>50% writes).
   *  Used for rolling bust detection: once consecutiveBusts reaches
   *  SUSTAINED_BUST_THRESHOLD, stop trying to compress and warn that the
   *  conversation is unsustainable. */
  consecutiveBusts: number;
  /** Consecutive turns where the upstream reported zero cache_creation_input_tokens.
   *  After >= NO_CACHE_WRITE_THRESHOLD turns, the session is treated as "free-write" —
   *  compression never busts an expensive cache, so shouldCompress() always says yes.
   *  Covers both free-write-cache providers (MiniMax) and non-caching providers.
   *  Not persisted to DB — rebuilds from live API responses (same rationale as
   *  consecutiveBusts: stale values from a prior process would be incorrect). */
  zeroCacheWriteTurns: number;
  /** EMA (0..1) of how often recent real turns were `prefix-rewrite` busts —
   *  i.e. how actively meta-distillation is rewriting the distilled prefix
   *  (messages[0/1]). Read by the cache warmer (getPrefixChurnRate) to suppress
   *  warming a churning prefix. Not persisted (rebuilds from live API responses,
   *  same rationale as consecutiveBusts). */
  prefixChurnEma: number;
  /** Number of transform() calls observed for this session in the current
   *  process. Starts at 0 for a freshly-tracked session and is NOT restored
   *  from the DB (same rationale as consecutiveBusts). Drives the cold-start
   *  grace window that gates bust-spiral alerting for the first
   *  COLD_START_GRACE_TURNS turns (#797). */
  transformCount: number;
  /** True once we've fired the high-severity Sentry alert for this session's
   *  current bust-spiral episode. Cleared when consecutiveBusts drops to 0
   *  (i.e. the episode ended) so a future spiral can alert again. NOT
   *  persisted to DB (same rationale as consecutiveBusts). */
  bustSpiralAlerted: boolean;
  /** True once we've emitted the cold-start info-breadcrumb for this session's
   *  first in-grace spiral. Cold-start is bounded by the grace window so this
   *  is never cleared within a process run. NOT persisted to DB. */
  bustSpiralColdStartLogged: boolean;

  /**
   * Distillation row snapshot — cached to avoid hitting the DB on every
   * transform() call. Refreshed only at turn boundaries (when a new user
   * message appears) or on first call / idle resume. During autonomous
   * tool-call chains this stays frozen, keeping the distilled prefix
   * byte-identical across consecutive API calls and preserving the prompt
   * cache.
   *
   * Cost context: each prefix refresh costs context_size × cache_write_price
   * (~$1.88 per bust at 500K Sonnet). New distillations have near-zero
   * marginal value mid-chain since the model already has raw messages.
   */
  distillationSnapshot: DistillationSnapshot | null;
  /**
   * Cross-turn dedup decisions, keyed by "<messageID>:<partID>" → wasCollapsed.
   * Keeps deduplicateToolOutputs() stable as the conversation grows: a tool
   * output already sent (full or collapsed) keeps that form so the prompt cache
   * isn't busted by a newly-appended later duplicate flipping an early message.
   */
  dedupDecisions: Map<string, boolean>;

  // --- Shared cache-economics inputs/result (see cache-economics.ts) ---
  // The single-entry-point design (evaluateCacheStrategy) reads every input for
  // the warm-vs-compact decision from this one place and stores the one result
  // here, so the cache warmer and the gradient bust calculator branch off an
  // identical decision and cannot diverge.
  /** Full-body token size from the most recent transform() (gradient's own estimate). */
  cacheSizeFull: number;
  /** Compacted-body token target from the most recent transform(); === cacheSizeFull when no compaction is available. */
  cacheSizeCompressed: number;
  /** Non-message floor (system/tools overhead + LTM) of the most recent
   *  transform(), in the SAME estimate basis as the message body (un-inflated).
   *  Compaction never removes this; it is added back to the compressed body so
   *  cacheSizeCompressed lands on the same scale as cacheSizeFull (issue #886). */
  cacheNonBodyTokens: number;
  /** Multiplier that maps the gradient's un-inflated (chars/3) body estimate onto
   *  cacheSizeFull's scale: 1 when calibrated, UNCALIBRATED_SAFETY when not — the
   *  same factor layer0Bounds applies to cacheSizeFull. (issue #886) */
  cacheBodySafety: number;
  /** The single stored strategy decision + when it was computed (null until first evaluated). */
  cacheStrategy: { result: CacheEconomicsResult; decidedAt: number } | null;
};

function makeSessionState(): SessionState {
  return {
    lastKnownInput: 0,
    lastKnownLtm: 0,
    lastKnownMessageCount: 0,
    lastTransformedCount: 0,
    lastLayer: 0,
    maxSustainedLayer: 0,
    lastWindowMessageIDs: new Set(),
    forceMinLayer: 0,
    lastTransformEstimate: 0,
    ltmTokens: 0,
    overheadEma: null,
    distilledBudgetHighWater: 0,
    prefixCache: null,
    rawWindowCache: null,
    lastTurnAt: 0,
    cameOutOfIdle: false,
    postIdleCompact: false,
    consecutiveHighLayer: 0,
    consecutiveBusts: 0,
    zeroCacheWriteTurns: 0,
    prefixChurnEma: 0,
    transformCount: 0,
    bustSpiralAlerted: false,
    bustSpiralColdStartLogged: false,

    distillationSnapshot: null,
    dedupDecisions: new Map(),
    cacheSizeFull: 0,
    cacheSizeCompressed: 0,
    cacheNonBodyTokens: 0,
    cacheBodySafety: 1,
    cacheStrategy: null,
  };
}

const sessionStates = new Map<string, SessionState>();

function getSessionState(sessionID: string): SessionState {
  let state = sessionStates.get(sessionID);
  if (!state) {
    state = makeSessionState();
    // Restore persisted forceMinLayer from DB — survives process restarts.
    // Critical for "prompt too long" recovery: the error handler sets
    // forceMinLayer=2, but if OpenCode restarts before the next turn,
    // the in-memory escalation would be lost without this.
    state.forceMinLayer = loadForceMinLayer(sessionID) as SafetyLayer;

    // Restore gradient calibration state from DB (v24) — avoids uncalibrated
    // first turns after restart. Without this, lastTurnAt=0 prevents
    // onIdleResume() from detecting idle gaps.
    //
    // Atomic restore: lastTurnAt > 0 is the proxy for "gradient state was
    // ever flushed to DB". Restore all fields together or none — avoids
    // per-field sentinel fragility where a valid value (e.g. lastLayer=0)
    // could be mistaken for "never persisted".
    const persisted = loadSessionTracking(sessionID);
    if (persisted && persisted.lastTurnAt > 0) {
      state.lastLayer = persisted.lastLayer as SafetyLayer;
      // Re-seed the monotonic layer ratchet from the restored layer. We don't
      // persist maxSustainedLayer separately (no migration): a session restored
      // at layer L (1-3) had sustained at least L, so lastLayer is a safe lower
      // bound for the floor. If the true high-water was higher, the ratchet
      // re-establishes within a turn.
      //
      // A restored lastLayer === 4 means the last turn was an emergency overflow
      // bust. Layer 4 itself never latches the ratchet (it is not a sustained
      // level), but the session WAS compressed before the emergency, so on resume
      // it must still hold the prefix — floor at >= 1 (matching the old
      // prefix-present floor's documented lastLayer===4 coverage). Without this,
      // a small resume turn would re-enter layer 0 and front-bust the prefix for
      // a turn until the next compressed turn re-latches the ratchet.
      if (state.lastLayer >= 1 && state.lastLayer <= 3) {
        state.maxSustainedLayer = state.lastLayer;
      } else if (state.lastLayer === 4) {
        state.maxSustainedLayer = 1;
      }
      state.lastKnownInput = persisted.lastKnownInput;
      // v43: restore the last-sent message count so the calibrated-delta
      // estimate identifies only genuinely-new messages on the resume turn
      // (without it, the whole conversation looks new → over-escalation). (#796)
      state.lastKnownMessageCount = persisted.lastKnownMessageCount;
      state.lastTurnAt = persisted.lastTurnAt;
      // Don't restore consecutiveBusts from DB — it's a short-term rolling
      // signal that must rebuild from live API responses in the current process.
      // Stale values from a previous process (different cache state after restart)
      // would cause false bust-spiral alerts. The dynamicContextCap column is
      // still written for diagnostics but not consumed on restore.
    }

    sessionStates.set(sessionID, state);
  }
  return state;
}

/**
 * Detect cold-cache resumption and refresh byte-identity caches.
 *
 * Anthropic's prompt cache evicts entries after ~5 minutes (default tier) /
 * ~1 hour (extended tier). When a session resumes after the eviction window,
 * the cache is provably cold — every prefix we've been carefully keeping
 * byte-stable (`prefixCache`, `rawWindowCache`, plus the host's per-session
 * LTM cache) provides no benefit on this turn. Worse, the LTM block was
 * scored against the conversation context as it was on the previous turn,
 * which may have drifted significantly in N hours.
 *
 * On resume after `thresholdMs` (cold cache — `skipCompact` false):
 *   - reset the distilled prefix cache (next turn re-renders from scratch)
 *   - reset the raw window pin cache (next turn picks a fresh cutoff)
 *   - set `cameOutOfIdle` so the OpenCode host can also clear `ltmSessionCache`
 *     and bypass the conversation-vs-LTM cost comparison in the LTM
 *     degraded-recovery branch
 *
 * When `skipCompact` is true the upstream cache is still warm, so the
 * byte-identity caches (prefix, raw-window pin, distilled high-water, dedup
 * memo) are PRESERVED — re-rendering/re-pinning them would bust the warm prefix
 * the warmer paid to keep alive. `cameOutOfIdle` is still set (the host's LTM
 * handling is unaffected) and `postIdleCompact` is left false.
 *
 * Importantly, this does NOT touch:
 *   - reasoning blocks (Anthropic's April 23 postmortem identifies dropping
 *     reasoning blocks as the root cause of forgetfulness/repetition; Lore
 *     preserves reasoning by policy across all gradient layers)
 *   - the gradient layer (cold cache doesn't change token budgets;
 *     calibration's actualInput = input + cache.read + cache.write already
 *     accounts for cache misses correctly)
 *   - calibration state (`lastKnownInput`, overhead EMA, message-ID set) —
 *     the next API response will refresh these via the normal calibrate() path
 *
 * Set `thresholdMs <= 0` to disable. Returns true if a reset fired so the
 * caller can log/observe.
 *
 * @param skipCompact  When true, the caller knows the upstream prompt cache is
 *   still warm (e.g. the cache warmer recently refreshed it across the idle
 *   gap). In that case onIdleResume PRESERVES the byte-identity caches (prefix,
 *   raw-window pin, distilled high-water, dedup memo) and does NOT set
 *   postIdleCompact — re-rendering the prefix or compacting would produce a
 *   different prompt body that no longer matches the warmed prefix, busting the
 *   cache and wasting the warming cost. It still drops the prewarm snapshot and
 *   sets cameOutOfIdle. When false (cache provably cold) all caches are reset.
 */
export function onIdleResume(
  sessionID: string,
  thresholdMs: number,
  now: number = Date.now(),
  skipCompact: boolean = false,
): { triggered: false } | { triggered: true; idleMs: number } {
  if (thresholdMs <= 0) return { triggered: false };
  const state = getSessionState(sessionID);
  if (state.lastTurnAt === 0) return { triggered: false }; // first turn — nothing to refresh
  const idleMs = now - state.lastTurnAt;
  if (idleMs < thresholdMs) return { triggered: false };
  // When the caller reports the upstream prompt cache is still warm
  // (`skipCompact` — e.g. the cache warmer kept it alive across the idle gap),
  // PRESERVE the byte-identity caches. Clearing them forces the next turn to
  // re-render the distilled prefix (busts messages[0/1]) and re-pin the raw
  // window (busts messages[2]) — throwing away the exact warm prefix the warmer
  // paid to maintain, and turning a warm resume into a full cache write. Only
  // when the cache is provably cold do we refresh them (a bust is unavoidable
  // then, so we take the opportunity to fold in fresh distillations / re-pin).
  if (!skipCompact) {
    state.prefixCache = null;
    state.rawWindowCache = null;
    // Cold cache → the prefix will be re-rendered/re-pinned, so drop the distilled
    // high-water too (Bug 1, lever 1; same lifecycle as the raw-window pin).
    state.distilledBudgetHighWater = 0;
    // Cache is cold after idle eviction — a fresh window will be rebuilt, so the
    // stable-dedup memo can reset too (its purpose is warm-cache stability).
    state.dedupDecisions.clear();
  }
  // The prewarm snapshot is a pure perf optimization (re-loaded on demand) and
  // does not affect the frozen prefix bytes — safe to drop on any idle resume.
  state.distillationSnapshot = null;
  state.cameOutOfIdle = true;
  state.postIdleCompact = !skipCompact;
  return { triggered: true, idleMs };
}

/**
 * Return the wall-clock timestamp (epoch ms) of the most recent transform()
 * call for this session. Returns 0 if the session has never been transformed.
 * Used by callers (e.g. meta-distillation gating) to check whether the
 * upstream prompt cache is likely still warm.
 */
export function getLastTurnAt(sessionID: string): number {
  return sessionStates.get(sessionID)?.lastTurnAt ?? 0;
}

/**
 * Read-and-clear the cameOutOfIdle flag. The OpenCode host's LTM degraded-
 * recovery branch consumes this to decide whether to bypass the
 * conversation-vs-LTM token comparison on a post-idle turn.
 */
export function consumeCameOutOfIdle(sessionID: string): boolean {
  const state = sessionStates.get(sessionID);
  if (!state?.cameOutOfIdle) return false;
  state.cameOutOfIdle = false;
  return true;
}

// ---------------------------------------------------------------------------
// Shared cache-economics: single entry point
// ---------------------------------------------------------------------------

/** Survival inputs supplied by the cache warmer for the shared strategy decision. */
export interface CacheSurvivalInputs {
  /** Probability the session returns within the warming horizon (0..1). */
  pReturn: number;
  /** Expected warmup cycles before the session resolves. */
  expectedCycles: number;
  /** Expected turns the resumed session runs (the "ongoing cost" horizon). */
  expectedFutureTurns: number;
  /**
   * #947 — meta-distillation threshold (gen-0 segments per meta cycle). Passed
   * through to `decideCacheStrategy` to compute the meta-aware adjustment to
   * `coolBustCost`. Default 0 (or undefined) → no adjustment, byte-identical
   * to the pre-#947 behavior. Use `cfg.distillation.metaThreshold`.
   */
  metaThreshold?: number;
  /**
   * #947 — per-call cost of meta-distillation ($/call, same currency unit as
   * `readPerToken`/`writePerToken`). Pre-computed at the call site from
   * worker model rates × estimated input/output token counts. Default 0 (or
   * undefined) → only the cache-read-miss term in the meta-bust cost applies
   * (the LLM term is suppressed).
   */
  metaDistillCostPerCall?: number;
}

/**
 * Compute the cache-economics compressed size on the SAME scale as cacheSizeFull
 * (issue #886). cacheSizeFull is INPUT-scale: it includes the non-message floor
 * (system/tools overhead + LTM) and is ×UNCALIBRATED_SAFETY-inflated on
 * uncalibrated turns. A compacted prompt still carries that whole floor — only
 * the message body shrinks — so the compressed cache size is:
 *
 *   (nonBodyFloor + rebuiltWindowBody) × bodySafety   (clamped to full)
 *
 * where `rebuiltWindowBody` (result.totalTokens) and `nonBodyFloor` are the
 * gradient's un-inflated estimates and `bodySafety` is the same factor applied to
 * full. Layer 0 = no compaction → full.
 *
 * EXACT for UNCALIBRATED turns: cacheSizeFull there is `(msgBody+overhead+ltm)·s`
 * built from the SAME `overhead+ltm` expression, so the floor cancels and the
 * delta is exactly `(msgBody−rebuiltBody)·s` (the removed message tokens).
 * APPROXIMATE for CALIBRATED turns: cacheSizeFull is the API-measured
 * `lastKnownInput`, whose embedded floor is the real tokenizer count, while this
 * floor is the gradient's `overhead+ltm` estimate (and `overhead` is an EMA that
 * already absorbs LTM + the chars/3 body undercount — the same entanglement as
 * `usable`). The mismatch errs toward a LARGER compressed (under-reports savings;
 * the clamp to `full` keeps it safe). Fully reconciling the calibrated basis is
 * the remaining PR2b normalization work; harmless while the evaluator is
 * shadow-only.
 */
export function computeCompressedCacheSize(
  layer: number,
  rebuiltWindowTokens: number,
  nonBodyTokens: number,
  bodySafety: number,
  fullTokens: number,
): number {
  if (layer < 1) return fullTokens;
  const compressed = Math.round(
    (Math.max(0, nonBodyTokens) + Math.max(0, rebuiltWindowTokens)) *
      Math.max(1, bodySafety),
  );
  return Math.max(0, Math.min(compressed, fullTokens));
}

/**
 * Record the gradient's own size estimate for a session's current body.
 *
 * This is the WRITER side and intentionally CREATES the session state (via
 * getSessionState) — in production the writer is transform() (which writes the
 * fields directly) and this exported helper is used by tests. The READ
 * accessors (getCacheStrategy / getCacheSizeSnapshot) and the warmer-facing
 * evaluateCacheStrategy use the non-creating `sessionStates.get` so a background
 * warmer can never materialize a phantom session. Do NOT call this from the
 * warmer for that reason. `compressed === full` means "no compaction available"
 * (collapses cool-bust into cool-full-write downstream).
 */
export function setCacheSizeSnapshot(
  sessionID: string,
  fullBodyTokens: number,
  compressedTokens: number,
): void {
  const state = getSessionState(sessionID);
  state.cacheSizeFull = Math.max(0, fullBodyTokens);
  state.cacheSizeCompressed = Math.max(
    0,
    Math.min(compressedTokens, fullBodyTokens),
  );
}

/**
 * THE single entry point for the warm-vs-compact decision. Reads every input
 * from one place — the gradient's size snapshot (set by transform), the cache
 * pricing (module state), and the survival inputs passed in by the warmer — runs
 * the pure decideCacheStrategy ONCE, stores the single result on the session
 * state, and returns it. Both the cache warmer and the gradient bust calculator
 * branch off this stored result (via getCacheStrategy) so they cannot disagree.
 *
 * Returns null when there is no size snapshot yet (the session has not been
 * transformed) so callers keep their legacy behaviour.
 */
export function evaluateCacheStrategy(
  sessionID: string,
  survival: CacheSurvivalInputs,
  // Per-token pricing. Callers SHOULD pass the session's own model pricing — the
  // module-global getCachePricing() reflects the LAST transform's model, which
  // can belong to a different session/model when a background warmer evaluates.
  pricing?: { readPerToken: number; writePerToken: number },
  now: number = Date.now(),
): CacheEconomicsResult | null {
  const state = sessionStates.get(sessionID);
  if (!state || state.cacheSizeFull <= 0) return null;
  // Only fall back to the module-global pricing when no override is supplied
  // (the override is the common path — callers SHOULD pass their own pricing).
  const result = decideCacheStrategy({
    fullBodyTokens: state.cacheSizeFull,
    compressedTokens: state.cacheSizeCompressed,
    readPerToken: pricing?.readPerToken ?? getCachePricing().read,
    writePerToken: pricing?.writePerToken ?? getCachePricing().write,
    pReturn: survival.pReturn,
    expectedCycles: survival.expectedCycles,
    expectedFutureTurns: survival.expectedFutureTurns,
    metaThreshold: survival.metaThreshold,
    metaDistillCostPerCall: survival.metaDistillCostPerCall,
  });
  state.cacheStrategy = { result, decidedAt: now };
  return result;
}

/** Read the single stored strategy decision for a session (null if never evaluated). */
export function getCacheStrategy(
  sessionID: string,
): { result: CacheEconomicsResult; decidedAt: number } | null {
  return sessionStates.get(sessionID)?.cacheStrategy ?? null;
}

/**
 * Read the gradient's last full/compressed size estimate for a session (null if
 * never transformed). Exposed so the warmer can log the shared sizes and
 * cross-check the gradient estimate against the real API token count.
 */
export function getCacheSizeSnapshot(
  sessionID: string,
): { full: number; compressed: number } | null {
  const state = sessionStates.get(sessionID);
  if (!state || state.cacheSizeFull <= 0) return null;
  return { full: state.cacheSizeFull, compressed: state.cacheSizeCompressed };
}

/**
 * The gradient layer the session's most recent real transform() landed on
 * (0 = raw passthrough … 4 = emergency). Returns null when the session has
 * never been transformed in-process (gradient has no state for it) so callers
 * fail open. Non-creating (`sessionStates.get`) so a background warmer can
 * never materialize a phantom session. Warmups replay a stored body and do NOT
 * call transform(), so this reflects the last genuine turn's compression state
 * — the signal the warmer uses to skip sessions whose prefix is being
 * aggressively re-rendered under emergency compaction.
 */
export function getLastTransformLayer(sessionID: string): number | null {
  const state = sessionStates.get(sessionID);
  return state ? state.lastLayer : null;
}

// LTM tokens injected via system transform hook this turn.
// Per-session when a sessionID is provided (preferred), with a module-level
// fallback for callers that don't have a session ID.
let ltmTokensFallback = 0;

/**
 * Snapshot of all model-derived budget inputs for a single transform.
 *
 * These values were previously only mutable module globals set per-request by
 * the host. Because the host does async work (ltm.forSession awaits) BETWEEN
 * setting them and calling transform(), a concurrently-running request for a
 * DIFFERENT model could clobber the globals on the single-threaded event loop,
 * so the transform read the wrong model's caps/pricing (the cross-model
 * contamination seen as l0cap flipping 200000 ↔ 3571428 and layer thrash).
 *
 * Passing a ModelBudget to transform() makes the per-request values atomic:
 * transform() applies them synchronously right before the (fully synchronous)
 * transformInner, so no other request can interleave between apply and use.
 * The setters remain for tests and legacy callers.
 */
export type ModelBudget = {
  contextLimit: number;
  outputReserved: number;
  maxLayer0Tokens: number;
  cacheWriteCostPerToken: number;
  cacheReadCostPerToken: number;
  /**
   * Optional per-model quality knee (fill fraction where degradation begins).
   * When omitted, the module default (DEFAULT_QUALITY_KNEE_FRACTION) is used.
   */
  qualityKneeFraction?: number;
};

/**
 * Atomically apply a per-request model budget to the module globals. Called at
 * the very top of transform() (synchronous path) so the values cannot be
 * clobbered by a concurrent request before the transform reads them.
 */
function applyModelBudget(budget: ModelBudget): void {
  setModelLimits({
    context: budget.contextLimit,
    output: budget.outputReserved,
  });
  setMaxLayer0Tokens(budget.maxLayer0Tokens);
  setCachePricing(budget.cacheWriteCostPerToken, budget.cacheReadCostPerToken);
  setQualityKnee(budget.qualityKneeFraction ?? DEFAULT_QUALITY_KNEE_FRACTION);
}

export function setModelLimits(limits: { context: number; output: number }) {
  contextLimit = limits.context || 200_000;
  // NOTE: this cap of 32K matches what @ai-sdk/anthropic sends as max_tokens for
  // claude-opus-4-6 (the SDK doesn't recognise the -6 variant and falls back to
  // the generic claude-opus-4- pattern with maxOutputTokens=32K).  If the SDK is
  // updated to send the model's actual limit (128K for opus-4-6), this cap will
  // become wrong — the effective max input would drop from 168K to 72K but our
  // budget would still assume 168K.  At that point, remove the cap.
  outputReserved = Math.min(limits.output || 32_000, 32_000);
}

/**
 * Set the cost-aware layer-0 token cap. When the cap > 0, the layer-0
 * passthrough gate uses `min(maxInput, cap)` instead of `maxInput` alone.
 *
 * Call from the host adapter after computing the cap from model pricing:
 * `cap = max(targetCostPerTurn / model.cost.cache.read, MIN_LAYER0_FLOOR)`
 */
export function setMaxLayer0Tokens(tokens: number) {
  maxLayer0Tokens = Math.max(0, Math.floor(tokens));
}

/**
 * Compute the layer-0 token cap from a per-turn cost target and cache-read
 * price.
 *
 * The raw formula `target$ / readCost` answers "how many tokens can I re-read
 * per turn for $target". For a cheap-cache model (readCost near zero) that is
 * an astronomically large number (tens of millions of tokens) with no physical
 * meaning — it silently disables the cap (which is then min()'d against the
 * real window and discarded). Clamp the result to the model's context window so
 * the cost cap can only ever make layer 0 *tighter* than the window, never
 * larger. Proactive compression for cheap models is driven by the fill-based
 * `qualityCostMultiplier`, not this cap.
 */
export function computeLayer0Cap(
  targetCostPerTurn: number,
  cacheReadCostPerToken: number,
  contextWindow?: number,
): number {
  if (targetCostPerTurn <= 0 || cacheReadCostPerToken <= 0) return 0;
  const rawCap = Math.floor(targetCostPerTurn / cacheReadCostPerToken);
  const ceiling =
    contextWindow && contextWindow > 0
      ? contextWindow
      : Number.POSITIVE_INFINITY;
  return Math.max(Math.min(rawCap, ceiling), MIN_LAYER0_FLOOR);
}

/** Called by the system transform hook after formatting LTM knowledge.
 *  When sessionID is provided, stores on per-session state to prevent
 *  cross-session budget contamination. Falls back to module-level global
 *  for callers without a session ID. */
export function setLtmTokens(tokens: number, sessionID?: string) {
  if (sessionID) {
    getSessionState(sessionID).ltmTokens = tokens;
  }
  ltmTokensFallback = tokens;
}

/** Returns the LTM token count for the given session, falling back to
 *  the module-level global when no session ID is provided. */
export function getLtmTokens(sessionID?: string): number {
  if (sessionID) {
    const state = sessionStates.get(sessionID);
    if (state) return state.ltmTokens;
  }
  return ltmTokensFallback;
}

/**
 * Returns the token budget available for LTM system-prompt injection.
 * This is the usable context (after output + overhead) multiplied by
 * the configured ltm budget fraction. Call this from the system transform
 * hook to cap how many tokens formatKnowledge may use.
 */
export function getLtmBudget(
  ltmFraction: number,
  sessionID?: string,
  opts?: { isSubagent?: boolean },
): number {
  const overhead = getOverhead(sessionID);
  const usable = Math.max(0, contextLimit - outputReserved - overhead);
  // Snap usable DOWN to the LTM_BUDGET_STEP grid before deriving anything.
  // Bucketing absorbs the per-turn overhead-EMA wobble so the budget — and thus
  // the ltm.forSession() packing boundary and the pinned system[2] set — stays
  // stable turn-to-turn. A wobbling boundary drops/re-adds the lowest-ranked
  // entry every turn, appending a durable prompt-delta into the cached prefix (a
  // cache bust). Floor (not round) so the ceiling below can never exceed its
  // fraction of the REAL window. Driving BOTH the fraction budget and the
  // ceiling off this one grid keeps the whole result stable, including the
  // ceiling-bound small-window path.
  //
  // Guard the sub-one-step window (0 < usable < LTM_BUDGET_STEP): flooring would
  // snap it to 0 and disable LTM entirely, which we never want on tiny-context
  // models. There, fall back to the real `usable` — the grid can't stabilize a
  // window smaller than one bucket anyway, and such a window is too small/cheap
  // for cache churn to matter.
  const bucketed = Math.floor(usable / LTM_BUDGET_STEP) * LTM_BUDGET_STEP;
  const gridUsable = bucketed > 0 ? bucketed : usable;
  const raw = Math.floor(gridUsable * ltmFraction);
  if (raw <= 0) return 0;

  // Context-aware ceiling: the safe share of the window a single LTM block may
  // take. On large models it caps only the "never disable" floor below (the
  // configured fraction is well under it); on small/short windows it stops that
  // floor from ballooning a small request into a large share of the context.
  // See #1300 context (sub-agent output crowding).
  const ceiling = Math.floor(
    gridUsable *
      (opts?.isSubagent
        ? SUBAGENT_MAX_LTM_BUDGET_FRACTION
        : MAX_LTM_BUDGET_FRACTION),
  );

  // Sub-agents are short-lived (1-3 turns): give them exactly the requested
  // fraction, capped by the tighter ceiling, so a focused task keeps its window
  // for real work instead of injected knowledge.
  if (opts?.isSubagent) return Math.min(raw, ceiling);

  // Main sessions: quantize the fraction-derived budget to the step (so large
  // windows are byte-identical to the historical value) and floor at one step
  // so LTM is never disabled on small-context models — but cap ONLY that floor
  // by the ceiling so a small window isn't flooded. The configured fraction
  // itself is never clipped, so a large `budget.ltm` passes through on large
  // models.
  const quantized = Math.round(raw / LTM_BUDGET_STEP) * LTM_BUDGET_STEP;
  const floor = Math.min(LTM_BUDGET_STEP, ceiling);
  return Math.max(floor, quantized);
}

/** Returns the token budget for stable LTM (preferences). Independent of context-bound LTM budget. */
export const getPreferenceLtmBudget = getLtmBudget;

// Called after each assistant message completes with real token usage data.
// actualInput    = tokens.input + tokens.cache.read + tokens.cache.write
// sessionID      = session that produced this response (for exact-tracking validity)
// messageCount   = number of messages that were sent (for delta estimation)
//
// Overhead calibration uses lastTransformEstimate (the token estimate from the
// compressed window that was actually sent to the model) instead of re-estimating
// all session messages. On compressed sessions, all-message estimate >> actualInput,
// which clamped overhead to 0 and broke budget calculations.
export function calibrate(
  actualInput: number,
  sessionID?: string,
  messageCount?: number,
) {
  const state = sessionID !== undefined ? getSessionState(sessionID) : null;

  // Use the transform's own estimate for the compressed window it produced.
  // This is the correct baseline: it estimates the same messages the model saw.
  const messageEstimate = state ? state.lastTransformEstimate : 0;

  // Lever 3 (Bug 1): subtract the LTM injected for this turn. `overhead` must
  // represent system prompt + tools ONLY — the `usable` computation subtracts
  // LTM SEPARATELY (sessLtmTokens). actualInput INCLUDES the LTM (it lives in
  // the system blocks) while messageEstimate (body only) does NOT, so leaving
  // LTM in `overhead` double-counts it and makes `usable` systematically too
  // small. This matches the documented intent at the `usable` computation.
  const ltm = state ? state.ltmTokens : 0;

  // Skip when actualInput > 0 but no transform estimate exists yet (no baseline
  // to compare against). Allow when both are 0 (test setup to zero overhead) or
  // when we have a real transform estimate.
  if (messageEstimate > 0 || actualInput === 0) {
    // Scale fix: `actualInput` is REAL provider tokens but `messageEstimate`
    // (lastTransformEstimate) is the chars/3 body estimate, which undercounts by
    // ~BODY_TOKEN_RATIO. Subtracting the un-inflated estimate leaked
    // ~0.40·body_real into `overhead`, so for large/growing sessions overhead
    // ballooned (~125K on a 200K model), `usable` collapsed (~25K), the session
    // was forced to Layer 4, and the distilled prefix re-rendered every turn
    // (the messages[1] cache bust). Inflate the body estimate to the real scale
    // first so `overhead` is TRUE system+tools only and stays stable as the body
    // grows. (messageEstimate===0 ⇒ overhead = actualInput − ltm, unchanged.)
    const realMessageEstimate = Math.round(messageEstimate * BODY_TOKEN_RATIO);
    const overhead = Math.max(0, actualInput - realMessageEstimate - ltm);
    // Lever 2 (Bug 1): the per-session EMA is authoritative — getOverhead()
    // prefers it. The module-global EMA is still updated, but only serves as
    // the first-turn seed / no-session fallback. A single shared global blended
    // heterogeneous concurrent sessions (different system-prompt sizes) into one
    // value, producing the ~20x turn-to-turn `usable` swing that front-busted
    // the distilled prefix. Both use the same 0.7/0.3 smoothing.
    const blend = (prev: number | null) =>
      prev === null ? overhead : Math.round(prev * 0.7 + overhead * 0.3);
    calibratedOverhead = blend(calibratedOverhead);
    if (state) state.overheadEma = blend(state.overheadEma);
  }

  // Store per-session exact counts for the proactive layer 0 decision.
  if (state) {
    state.lastKnownInput = actualInput;
    state.lastKnownLtm = state.ltmTokens;
    if (messageCount !== undefined) state.lastKnownMessageCount = messageCount;
  }
}

/**
 * Returns the calibrated overhead (system prompt + tools, in real tokens) for a
 * session. Prefers the per-session EMA (Bug 1, lever 2); falls back to the
 * module-global EMA (first-turn seed / no-session callers), then to
 * FIRST_TURN_OVERHEAD before any calibration. Uses a non-creating Map lookup so
 * read-only callers never spawn a phantom SessionState.
 */
export function getOverhead(sessionID?: string): number {
  if (sessionID !== undefined) {
    const ema = sessionStates.get(sessionID)?.overheadEma;
    if (ema != null) return ema;
  }
  return calibratedOverhead ?? FIRST_TURN_OVERHEAD;
}

/**
 * Returns the number of messages in the most recent transform() output for
 * the given session. Used by calibrate() to track the compressed window size.
 */
export function getLastTransformedCount(sessionID: string): number {
  return sessionStates.get(sessionID)?.lastTransformedCount ?? 0;
}

/** Returns the token estimate from the most recent transform() output. */
export function getLastTransformEstimate(sessionID: string): number {
  return sessionStates.get(sessionID)?.lastTransformEstimate ?? 0;
}

/** Returns the layer used by the most recent transform() call. For testing. */
export function getLastLayer(sessionID?: string): SafetyLayer {
  if (sessionID) return sessionStates.get(sessionID)?.lastLayer ?? 0;
  // Fallback for tests: return from the first (and usually only) session state
  const first = sessionStates.values().next().value;
  return first?.lastLayer ?? 0;
}

/**
 * Distilled-prefix budget hysteresis (Bug 1, lever 1).
 *
 * Returns the budget the layer-1 prefix-trim gate should use: the larger of the
 * current stage budget and the session's high-water mark. Once the distilled
 * prefix has been admitted at some `distilledBudget`, residual per-turn
 * overhead-EMA wobble must not contract the budget below the frozen prefix and
 * re-trim it (a front-bust). Mirrors the raw-window pin's high-water hysteresis.
 * Pure function of its inputs.
 */
export function effectiveDistilledBudget(
  stageDistBudget: number,
  highWater: number,
): number {
  return Math.max(stageDistBudget, highWater);
}

/**
 * Force the next transform() call for this session to use at least the given layer.
 * Called when the API returns "prompt is too long" so the next attempt
 * trims the context enough to fit within the model's context window.
 */
export function setForceMinLayer(layer: SafetyLayer, sessionID?: string) {
  if (sessionID) {
    getSessionState(sessionID).forceMinLayer = layer;
    saveForceMinLayer(sessionID, layer);
  } else {
    // Fallback for tests / callers without session ID: set on all active sessions
    for (const [sid, state] of sessionStates.entries()) {
      state.forceMinLayer = layer;
      saveForceMinLayer(sid, layer);
    }
  }
}

/**
 * Evict a single session's in-memory state. Called when a session has been
 * idle long enough that keeping its caches resident is wasteful. All
 * important state (gradient calibration, force-min-layer) is already
 * persisted to SQLite and will be reloaded if the session resumes.
 *
 * Does NOT reset global calibration — only frees session-specific caches
 * (prefix cache, raw window cache, distillation snapshot, etc.).
 */
export function evictSession(sessionID: string): void {
  sessionStates.delete(sessionID);
}

// For testing only — reset all calibration and force-escalation state
export function resetCalibration(sessionID?: string) {
  calibratedOverhead = null;
  cacheWriteCostPerToken = 0;
  cacheReadCostPerToken = 0;
  qualityKneeFraction = DEFAULT_QUALITY_KNEE_FRACTION;
  urgentDistillationEnabled = true;
  urgentDistillationMap.clear();
  if (sessionID) {
    saveForceMinLayer(sessionID, 0); // clear persisted state
    sessionStates.delete(sessionID);
  } else {
    for (const sid of sessionStates.keys()) {
      saveForceMinLayer(sid, 0);
    }
    sessionStates.clear();
  }
}

/**
 * For testing only — observe session-state cache fields without exposing the
 * full type. Returns null when the session has no state. Boolean fields answer
 * "does this cache hold something right now?" — sufficient for asserting that
 * onIdleResume() reset them. Numeric fields (`lastKnownMessageCount`,
 * `transformCount`, `overheadEma`, `distilledBudgetHighWater`) expose the
 * calibration state used by Bug 1's levers (per-session overhead isolation +
 * distilled-prefix budget hysteresis).
 */
export function inspectSessionState(sessionID: string): {
  hasPrefixCache: boolean;
  hasRawWindowCache: boolean;
  cameOutOfIdle: boolean;
  postIdleCompact: boolean;
  lastTurnAt: number;
  distillationSnapshot: DistillationSnapshot | null;
  consecutiveBusts: number;
  zeroCacheWriteTurns: number;
  prefixChurnEma: number;
  lastKnownMessageCount: number;
  transformCount: number;
  bustSpiralAlerted: boolean;
  bustSpiralColdStartLogged: boolean;
  overheadEma: number | null;
  distilledBudgetHighWater: number;
} | null {
  const state = sessionStates.get(sessionID);
  if (!state) return null;
  return {
    hasPrefixCache: state.prefixCache !== null,
    hasRawWindowCache: state.rawWindowCache !== null,
    cameOutOfIdle: state.cameOutOfIdle,
    postIdleCompact: state.postIdleCompact,
    lastTurnAt: state.lastTurnAt,
    distillationSnapshot: state.distillationSnapshot,
    consecutiveBusts: state.consecutiveBusts,
    zeroCacheWriteTurns: state.zeroCacheWriteTurns,
    prefixChurnEma: state.prefixChurnEma,
    lastKnownMessageCount: state.lastKnownMessageCount,
    transformCount: state.transformCount,
    bustSpiralAlerted: state.bustSpiralAlerted,
    bustSpiralColdStartLogged: state.bustSpiralColdStartLogged,
    overheadEma: state.overheadEma,
    distilledBudgetHighWater: state.distilledBudgetHighWater,
  };
}

/**
 * Return the prefix-churn rate (0..1 EMA) for a session (read-only). High values
 * mean meta-distillation is actively rewriting the distilled prefix, so warming
 * the prefix is wasteful (the next real turn will replace it). Returns 0 when the
 * session has no in-memory state — the safe default (no churn → warming allowed).
 *
 * Uses Map.get() (not getSessionState) to avoid creating phantom SessionState
 * entries — same rationale as getConsecutiveBusts.
 */
export function getPrefixChurnRate(sessionID: string): number {
  return sessionStates.get(sessionID)?.prefixChurnEma ?? 0;
}

/**
 * Return the consecutive-bust counter for a session (read-only).
 * Returns 0 if the session has no in-memory state — callers treat this
 * as "no bust pressure" which is the safe default.
 *
 * Uses Map.get() instead of getSessionState() to avoid creating phantom
 * SessionState entries with zeroed calibration fields, which would cause
 * the next transform() call to treat the session as uncalibrated.
 */
export function getConsecutiveBusts(sessionID: string): number {
  return sessionStates.get(sessionID)?.consecutiveBusts ?? 0;
}

// ---------------------------------------------------------------------------
// Bust-spiral alerting (#797)
//
// When a session goes through a sustained sequence of cache busts (the
// "bust spiral"), it's almost always a real caching bug we want to
// investigate — prefix drift, prompt-delta position drift after
// post-idle recompression, LTM pin mismatch, etc. We surface this via
// a host-registered hook (gateway → Sentry) rather than the user-facing
// warning we used to inject, because:
//   - The user has no actionable response.
//   - Two structurally different bugs (drift vs. genuine unbounded growth)
//     surfaced the same way to the user; Sentry's structured context lets
//     us distinguish them in aggregate.
//   - The cold-start opening (turn-1 cold write, turn-2 LTM injection,
//     turn-3 layer transition) is EXPECTED per #796/#804 and would be noise
//     if alerted on. The grace window above distinguishes the two cases.
//
// The hook is optional; core has no Sentry dependency. The gateway wires it
// once at startup (`setupBustSpiralCapture`).
// ---------------------------------------------------------------------------

/** Diagnostic snapshot passed to {@link BustSpiralHook} callbacks. */
export interface BustSpiralInfo {
  sessionID: string;
  /** Current consecutive-bust count at the time of detection. */
  consecutiveBusts: number;
  /** Transform count at the time of detection (cold-start gate signal). */
  transformCount: number;
  /** Current gradient layer (0 = raw passthrough, 4 = emergency). */
  layer: number;
  /** True when this transform took the cap-fit passthrough path (see
   *  TransformResult.capFit). Propagated so gateway-side dashboards can
   *  distinguish "session fits with headroom but is busting structurally"
   *  from "session is over the cap and busting from growth" without needing
   *  to re-derive the path from layer+totalTokens. */
  capFit: boolean;
}

/** Optional hook the gateway registers to surface bust-spiral events.
 *  All methods are optional; core calls only what the host provides. */
export interface BustSpiralHook {
  /** Within cold-start grace, busts first cross SUSTAINED_BUST_THRESHOLD.
   *  Fires once per cold-start episode (debounced via SessionState). */
  onColdStart?(info: BustSpiralInfo): void;
  /** Past cold-start grace, busts at SUSTAINED_BUST_THRESHOLD+. Fires once
   *  per spiral episode; cleared when consecutiveBusts drops to 0. */
  onSpiral?(info: BustSpiralInfo): void;
  /** Spiral ended (consecutiveBusts dropped to 0 after a spiral). Optional
   *  recovery signal — gateway typically emits an info-level breadcrumb. */
  onRecovered?(info: BustSpiralInfo): void;
}

let bustSpiralHook: BustSpiralHook | null = null;

/** Register (or clear, with `null`) the bust-spiral hook. The gateway calls
 *  this once at startup. Pass `null` to clear (used by tests). */
export function setBustSpiralHook(h: BustSpiralHook | null): void {
  bustSpiralHook = h;
}

/** Build a {@link BustSpiralInfo} snapshot from current SessionState. */
function bustSpiralInfo(
  sid: string,
  state: SessionState,
  result: TransformResult,
): BustSpiralInfo {
  return {
    sessionID: sid,
    consecutiveBusts: state.consecutiveBusts,
    transformCount: state.transformCount,
    layer: result.layer,
    capFit: result.capFit,
  };
}

/** Run the bust-spiral detection logic for a session. Called from
 *  transform() after the inner transform updates SessionState.
 *
 *  Fires:
 *  - `onColdStart` (once per cold-start episode) when in grace AND busts ≥
 *    SUSTAINED_BUST_THRESHOLD AND not already logged this episode.
 *  - `onSpiral` (once per past-grace episode) when past grace AND busts ≥
 *    SUSTAINED_BUST_THRESHOLD AND not already alerted this episode.
 *  - `onRecovered` when an alerted episode ends (busts dropped to 0).
 *
 *  Pure no-op when `sid` is undefined (callers without session context are not
 *  interesting for spiral alerts). */
function maybeDetectBustSpiral(
  sid: string | undefined,
  state: SessionState,
  result: TransformResult,
): void {
  if (!sid) return;
  const busts = state.consecutiveBusts;
  const inGrace = state.transformCount <= COLD_START_GRACE_TURNS;

  // Cap-fit suppression: when transformInner took the cap-fit passthrough
  // path (layer 0, fits layer-0 cap with headroom, fits under hard ceiling),
  // the result is marked `capFit: true`. Busts there come from structural
  // causes (e.g. prompt-delta position drift after post-idle recompression),
  // not genuine context growth — the original code hardcoded
  // `unsustainable: false` here. Faithfully mirrored via the explicit field
  // (NOT post-hoc `layer === 0 && getTier(totalTokens) === 0`, which is
  // structurally looser and would also mask tier-gate bypass sessions with
  // large LTM — see #952). Cold-start breadcrumb still fires for cap-fit
  // sessions; only the past-grace spiral alert is suppressed.
  const isCapFit = result.capFit;

  if (busts >= SUSTAINED_BUST_THRESHOLD) {
    if (inGrace && !state.bustSpiralColdStartLogged) {
      state.bustSpiralColdStartLogged = true;
      // Cold-start breadcrumb fires even for cap-fit sub-cap sessions —
      // useful telemetry for the cold-start phase regardless of cap-fit.
      bustSpiralHook?.onColdStart?.(bustSpiralInfo(sid, state, result));
    } else if (!inGrace && !state.bustSpiralAlerted && !isCapFit) {
      state.bustSpiralAlerted = true;
      bustSpiralHook?.onSpiral?.(bustSpiralInfo(sid, state, result));
    }
    return;
  }

  // Recovery — an alerted episode ended. Clear the bustSpiralAlerted
  // debounce so a future spiral alerts again. bustSpiralColdStartLogged is
  // intentionally NOT cleared: the cold-start grace window is bounded by
  // transformCount for the lifetime of the process, and within that window
  // we want a single breadcrumb per session, not one per bust→recovery→bust
  // cycle. Clearing it here would allow multiple onColdStart fires for the
  // same session within the same cold-start phase (#952 follow-up to #797).
  if (busts === 0) {
    const hadAlert = state.bustSpiralAlerted;
    state.bustSpiralAlerted = false;
    if (hadAlert) {
      bustSpiralHook?.onRecovered?.(bustSpiralInfo(sid, state, result));
    }
  }
}

/** Bust-pressure threshold for meta-distillation: consecutive busts ≥ this
 *  value trigger earlier consolidation of gen-0 segments. */
export const BUST_PRESSURE_THRESHOLD = 3;

/** Minimum gap between the session's last turn and "now" for the bust-pressure
 *  meta-threshold override (and the symmetric cool-bust mid-flight defer
 *  gate in the gateway idle handler) to apply. Below this, the session is too
 *  fresh — the user is likely to come back to a still-warming cache, and
 *  forcing meta would rewrite the prefix right before the next user turn,
 *  busting the cache they just paid to re-warm. Matches the default Anthropic
 *  prompt cache TTL (5 min) — anything tighter would race the warmer. */
export const DEEP_IDLE_MS = 5 * 60 * 1000;

/** Floor for the bust-pressure meta-threshold override. Even with busts
 *  ≥ BUST_PRESSURE_THRESHOLD, the lowered threshold is clamped to this value
 *  — meta-distillation is a heavy rewrite (archives gen-0, creates gen-1,
 *  busts the prompt cache on the next turn), so the override only fires
 *  when there's enough accumulated churn to justify it. With the default
 *  metaThreshold=20 and the 1/4 rule, the lowered value would be 5 — too
 *  eager given that meta itself causes prefix-rewrite busts. */
const BUST_PRESSURE_META_FLOOR = 10;

/**
 * Compute the effective meta-distillation threshold under bust pressure.
 *
 * Two conditions gate the override:
 *
 * (1) **Bust pressure** — `busts >= BUST_PRESSURE_THRESHOLD` (3 consecutive
 *     busts). The session is churning, so earlier consolidation can help.
 *
 * (2) **Deep-idle signal** — the session's last turn was at least
 *     `DEEP_IDLE_MS` (5 min) ago. Without this, a churning session that
 *     briefly goes idle (e.g. 90s gap) would fire meta right before the
 *     user comes back, busting the cache they just paid to re-warm. The
 *     ses_10f932a26ffe… (lore 0XrHNdlsWwgVkX4MH) incident: bust pressure
 *     crossed the threshold at 20:04, the session idled 36 min (well past
 *     5 min), and meta fired during that idle. Good. But the OLD logic
 *     (floor=3) would have fired meta at 20:04 even with a 1-min gap —
 *     which would have busted the cache on the turn the user was about to
 *     send. The 5-min gate is the "we expect the user to be away" check.
 *
 * When the override DOES fire, the threshold is `floor(configThreshold / 4)`
 * clamped to `BUST_PRESSURE_META_FLOOR` (10). The floor prevents the
 * override from collapsing the meta cadence to "every 5 gen-0 segments" —
 * which is too eager given that meta itself causes prefix-rewrite busts
 * (those are now exempt from consecutiveBusts, but they still incur a
 * real cache write cost).
 *
 * @param busts           - current consecutive-bust count for the session
 * @param configThreshold - configured metaThreshold (e.g. `cfg.distillation.metaThreshold`)
 * @param lastTurnAtMs    - wall-clock ms of the session's last turn
 *                          (use 0 for "never"; treated as deep-idle)
 * @param nowMs           - current wall-clock ms; injectable for deterministic
 *                          tests. Defaults to `Date.now()`. Production callers
 *                          never pass it.
 * @returns the effective meta-threshold for the idle handler
 */
export function effectiveMetaThreshold(
  busts: number,
  configThreshold: number,
  lastTurnAtMs = 0,
  nowMs: number = Date.now(),
): number {
  if (busts < BUST_PRESSURE_THRESHOLD) return configThreshold;
  // 0 = never set (first turn). Treat as deep-idle so the very first bust
  // pressure event isn't blocked by a missing timestamp.
  const gapMs = lastTurnAtMs === 0 ? Infinity : nowMs - lastTurnAtMs;
  if (gapMs < DEEP_IDLE_MS) return configThreshold;
  return Math.max(BUST_PRESSURE_META_FLOOR, Math.floor(configThreshold / 4));
}

/**
 * For testing only — set the session's lastTurnAt field. Used to simulate
 * idle gaps without sleeping. Creates the session state if not present so
 * tests don't need to seed it via a transform() call.
 */
export function setLastTurnAtForTest(sessionID: string, ms: number): void {
  getSessionState(sessionID).lastTurnAt = ms;
}

/**
 * For testing only — set the session's consecutiveBusts count. Used to drive
 * bust-pressure code paths (e.g. the idle handler's deferred-prefix-work
 * override) without replaying real cache-bust turns. Creates the session state
 * if not present.
 */
export function setConsecutiveBustsForTest(
  sessionID: string,
  busts: number,
): void {
  getSessionState(sessionID).consecutiveBusts = busts;
}

/**
 * For testing only — set the session's prefix-churn EMA (0..1). Used to drive the
 * cache warmer's prefix-churn gate without replaying real prefix-rewrite turns.
 * Creates the session state if not present.
 */
export function setPrefixChurnForTest(sessionID: string, churn: number): void {
  getSessionState(sessionID).prefixChurnEma = churn;
}

/**
 * Test-only: set the per-session `lastLayer` (the layer the most recent
 * transform() landed on) that {@link getLastTransformLayer} reports. Lets the
 * warmer's emergency-compaction gate be exercised without running a full
 * transform() to force the session into an emergency layer.
 */
export function setLastTransformLayerForTest(
  sessionID: string,
  layer: SafetyLayer,
): void {
  getSessionState(sessionID).lastLayer = layer;
}

/**
 * Test-only: set the per-session transform counter that drives the cold-start
 * grace window (#797). Set it above COLD_START_GRACE_TURNS to simulate a
 * session that has moved past cold-start stabilization, or below it to simulate
 * a freshly-tracked session still within the grace window.
 */
export function setTransformCountForTest(
  sessionID: string,
  count: number,
): void {
  getSessionState(sessionID).transformCount = count;
}

/**
 * Persist gradient calibration state to the session_state table.
 *
 * Designed to be called periodically (e.g. every 30s from the idle scheduler
 * tick) rather than on every mutation, to avoid write amplification on the
 * hot path. Max data loss on crash is one tick interval (~30s).
 */
export function saveGradientState(sessionID: string): void {
  const state = sessionStates.get(sessionID);
  if (!state) return;

  saveSessionTracking(sessionID, {
    lastLayer: state.lastLayer,
    lastKnownInput: state.lastKnownInput,
    lastKnownMessageCount: state.lastKnownMessageCount,
    lastTurnAt: state.lastTurnAt,
    // Repurpose the dead dynamicContextCap column (v24, always 0 now)
    // to persist consecutiveBusts — avoids a new DB migration.
    dynamicContextCap: state.consecutiveBusts,
  });
}

type Distillation = {
  id: string;
  observations: string;
  generation: number;
  token_count: number;
  created_at: number;
  session_id: string;
  r_compression: number | null;
  c_norm: number | null;
  source_ids: string[];
};

// Build the non-archived distillation query shared by {@link loadDistillations}
// and its off-thread twin {@link loadDistillationsOffloaded}, so both run
// byte-identical SQL + params (only the execution thread differs).
//
// id tie-break — never let same-ms rows reorder and bust the cache.
// created_at is Date.now() (ms precision); two rows written in the same
// millisecond would otherwise have an undefined relative order across
// queries, flipping the distilledPrefixCached validity anchor and forcing
// an unnecessary full prefix re-render. Distillation ids are random
// (crypto.randomUUID, v4), so (created_at, id) is NOT chronological for
// same-ms rows — but it IS a stable, deterministic total order, which is
// exactly what cache stability requires.
function distillationsQuery(
  pid: string,
  sessionID?: string,
): { sql: string; params: ReadParam[] } {
  const sql = sessionID
    ? "SELECT id, observations, generation, token_count, created_at, session_id, r_compression, c_norm, source_ids FROM distillations WHERE project_id = ? AND session_id = ? AND archived = 0 ORDER BY created_at ASC, id ASC"
    : "SELECT id, observations, generation, token_count, created_at, session_id, r_compression, c_norm, source_ids FROM distillations WHERE project_id = ? AND archived = 0 ORDER BY created_at ASC, id ASC";
  return { sql, params: sessionID ? [pid, sessionID] : [pid] };
}

type RawDistillationRow = Omit<Distillation, "source_ids"> & {
  source_ids: string;
};

function hydrateDistillationRow(r: RawDistillationRow): Distillation {
  return { ...r, source_ids: r.source_ids ? JSON.parse(r.source_ids) : [] };
}

// Load non-archived distillations for the in-context prefix.
// Archived gen-0 entries (preserved after meta-distillation) are excluded here
// but remain searchable via the recall tool's searchDistillations().
function loadDistillations(
  projectPath: string,
  sessionID?: string,
): Distillation[] {
  const pid = ensureProject(projectPath);
  const { sql, params } = distillationsQuery(pid, sessionID);
  const rows = db()
    .query(sql)
    .all(...params) as RawDistillationRow[];
  return rows.map(hydrateDistillationRow);
}

// Off-thread twin of {@link loadDistillations}: runs the identical (unbounded,
// session-length-growing) distillation scan through the read-worker pool so it
// doesn't block the main event loop on the pre-upstream critical path. #1082.
//
// Returns the same rows `loadDistillations(projectPath, sessionID)` would, or
// `null` on a worker TIMEOUT — the caller (prewarm) then leaves the snapshot
// untouched so `transform()`'s in-process `loadDistillationsCached` does the
// identical sync load (no behavior change, just no offload benefit that turn).
async function loadDistillationsOffloaded(
  projectPath: string,
  sessionID: string,
): Promise<Distillation[] | null> {
  const pid = ensureProject(projectPath);
  const { sql, params } = distillationsQuery(pid, sessionID);
  const rows = await offloadAllOrTimeout(sql, params);
  if (rows === READ_JOB_TIMED_OUT) return null;
  return (rows as RawDistillationRow[]).map(hydrateDistillationRow);
}

// Cached distillation loader — avoids hitting the DB on every transform() call.
// Refreshed only at turn boundaries (when a new user message appears), on first
// call (null snapshot), or after idle resume (snapshot cleared by onIdleResume).
// During autonomous tool-call chains (consecutive assistant→tool→assistant with
// the same last user message), returns the cached rows so the distilled prefix
// stays byte-identical and preserves the Anthropic prompt cache.
function loadDistillationsCached(
  projectPath: string,
  sessionID: string,
  messages: MessageWithParts[],
  sessState: SessionState,
): Distillation[] {
  // Find the last user message ID in the input (shared with prewarm so both
  // compute an identical turn-boundary cache key).
  const lastUserMsgId = lastUserMessageId(messages);

  const snapshot = sessState.distillationSnapshot;

  // Cache hit: same user message = still in the same tool-call chain
  if (snapshot && snapshot.lastUserMsgId === lastUserMsgId) {
    return snapshot.rows;
  }

  // Cache miss: new user message (turn boundary), first call, or post-idle
  const rows = loadDistillations(projectPath, sessionID);
  sessState.distillationSnapshot = { rows, lastUserMsgId };

  log.info(
    `distillation refresh: ${rows.length} rows` +
      ` (user msg ${lastUserMsgId?.substring(0, 16) ?? "none"})`,
  );

  return rows;
}

/**
 * Find the last user message id in `messages` — the turn-boundary cache key used
 * by both {@link loadDistillationsCached} and {@link prewarmDistillationSnapshot}.
 */
function lastUserMessageId(messages: MessageWithParts[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].info.role === "user") return messages[i].info.id;
  }
  return null;
}

/**
 * Pre-load the session's distillation snapshot OFF-THREAD before the (sync)
 * `transform()` runs, so the unbounded distillation scan stays off the
 * pre-upstream critical path. #1082 (part of #1077 workstream B).
 *
 * `transform()` is synchronous, so it cannot await the read itself. Instead the
 * pipeline (which is async) calls this first: it runs the SAME turn-boundary
 * cache-key check as `loadDistillationsCached` and, on a cache miss, loads the
 * distillations via the read-worker pool and populates
 * `sessState.distillationSnapshot`. `transform()`'s later
 * `loadDistillationsCached` then finds a matching snapshot and returns it
 * WITHOUT touching the DB.
 *
 * Zero behavior change — the rows and ordering are exactly what the sync load
 * would produce:
 *   - cache hit (same last user message → still in a tool-call chain): no-op.
 *   - cache miss + pool serves/falls back in-process: snapshot populated.
 *   - cache miss + worker TIMEOUT: snapshot left untouched, so `transform()`
 *     does the identical in-process load (no offload benefit this turn).
 *
 * Uses the same `getSessionState(sessionID)` singleton `transform()` reads, so
 * the populated snapshot is visible to it. `ensureProject()` runs on the main
 * thread inside the offloaded loader; only the scan is offloaded.
 */
export async function prewarmDistillationSnapshot(
  projectPath: string,
  sessionID: string | undefined,
  messages: MessageWithParts[],
): Promise<void> {
  if (!sessionID) return;
  const sessState = getSessionState(sessionID);
  const lastUserMsgId = lastUserMessageId(messages);

  // Cache hit: same user message = still in the same tool-call chain → the sync
  // loadDistillationsCached will reuse this snapshot; nothing to preload.
  const snapshot = sessState.distillationSnapshot;
  if (snapshot && snapshot.lastUserMsgId === lastUserMsgId) return;

  const rows = await loadDistillationsOffloaded(projectPath, sessionID);
  if (rows === null) return; // worker timeout → let transform() do the sync load

  sessState.distillationSnapshot = { rows, lastUserMsgId };
  log.info(
    `distillation prewarm: ${rows.length} rows` +
      ` (user msg ${lastUserMsgId?.substring(0, 16) ?? "none"})`,
  );
}

// Strip all <system-reminder>...</system-reminder> blocks from message text.
// For the user-message wrapper pattern, extracts the actual user text.
// For all other reminders (build-switch, plan reminders, etc.), drops them entirely.
// These tags are added by OpenCode in-memory or persisted as synthetic parts —
// leaving them in the raw window causes the model to echo the format.
function stripSystemReminders(text: string): string {
  return text
    .replace(/<system-reminder>[\s\S]*?<\/system-reminder>\n?/g, (match) => {
      const inner = match.match(
        /The user sent the following message:\n([\s\S]*?)\n\nPlease address/,
      );
      return inner ? `${inner[1].trim()}\n` : "";
    })
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function cleanParts(parts: LorePart[]): LorePart[] {
  const cleaned = parts.map((part) => {
    if (!isTextPart(part)) return part;
    const text = stripSystemReminders(part.text);
    if (text === part.text) return part;
    return { ...part, text };
  });
  // Filter out text parts that became empty after stripping
  const filtered = cleaned.filter(
    (part) => !isTextPart(part) || part.text.trim().length > 0,
  );
  // If all parts were stripped (e.g. a user message that was purely build-switch synthetic
  // content), keep a minimal placeholder so the message survives toModelMessages.
  // Without this, the message gets dropped and the conversation ends with an assistant message,
  // causing Anthropic's "does not support assistant message prefill" error.
  if (filtered.length === 0 && parts.length > 0) {
    const first = parts[0];
    if (isTextPart(first)) {
      return [{ ...first, text: "..." }];
    }
  }
  return filtered.length > 0 ? filtered : parts;
}

// Upper bound on how much of the output the path-extraction regex scans.
// Two mitigations for catastrophic backtracking in `PATH_RE`:
//   1. Skip entirely if the input contains no '/' (a path requires at least
//      one separator, so without one the regex has no possible match yet
//      still backtracks O(n²) on long runs of [\w.-]).
//   2. Cap the scanned slice at this limit so even crafted inputs with a
//      '/' somewhere don't stall the worker. The annotation only needs a
//      few representative paths — sampling the first 64KB is plenty.
const ANNOTATION_PATH_SCAN_LIMIT = 64 * 1024;
const PATH_RE = /(?:[\w.-]+\/)+[\w.-]+\.\w{1,5}/g;

// Build a metadata annotation for a stripped tool output, preserving key signals
// about what was lost without requiring an LLM call. Inspired by the per-token
// scalar bias β from "Fast KV Compaction via Attention Matching" (Zweiger et al.,
// 2025) — when tokens are removed, preserving metadata about the removed content
// helps the model compensate for information loss and decide whether to recall.
// Reference: https://arxiv.org/abs/2602.16284
export function toolStripAnnotation(toolName: string, output: string): string {
  const lines = output.split("\n").length;

  // Detect key signals via lightweight heuristics — no LLM call
  const hasError =
    /\b(?:error|fail(?:ed|ure)?|exception|panic|traceback)\b/i.test(output);

  // Path extraction: skip entirely if no '/' is present (cheap O(n) check
  // via indexOf) to avoid PATH_RE's O(n²) backtracking on long runs of
  // [\w.-] without a separator. Otherwise sample the first N KB.
  let uniquePaths: string[] = [];
  if (output.indexOf("/") !== -1) {
    const pathScan =
      output.length > ANNOTATION_PATH_SCAN_LIMIT
        ? output.slice(0, ANNOTATION_PATH_SCAN_LIMIT)
        : output;
    const paths = pathScan.match(PATH_RE);
    if (paths) uniquePaths = [...new Set(paths)].slice(0, 5);
  }

  let annotation = `[output omitted — ${toolName}: ${lines} lines`;
  if (hasError) annotation += ", contained errors";
  if (uniquePaths.length > 0)
    annotation += `, paths: ${uniquePaths.join(", ")}`;
  annotation += " — use recall for details]";
  return annotation;
}

// ---------------------------------------------------------------------------
// Content-aware deduplication
// ---------------------------------------------------------------------------
// Inspired by Dirac's ContextManager file-read deduplication: detects when the
// same content appears multiple times in the conversation (e.g., the same file
// read multiple times, or the same command output repeated) and replaces earlier
// occurrences with compact annotations. This reduces token pressure before layer
// selection, potentially keeping sessions at lower (less lossy) gradient layers.

// Minimum output size (chars) to consider for dedup — annotations for smaller
// outputs would cost more tokens than the original content.
const DEDUP_MIN_CHARS = 600;

/** Fast FNV-1a hash for content comparison. */
function simpleHash(str: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash;
}

/** Parsed read-tool input: file path plus optional line range. */
type ReadRange = {
  path: string;
  /** 1-based start line. undefined = from beginning. */
  offset: number | undefined;
  /** Number of lines to read. undefined = to end. */
  limit: number | undefined;
};

/** Extract file path from a tool's input JSON.
 *  Handles common formats: {"path": "/foo.ts"}, {"filePath": "/foo.ts"},
 *  and plain text fallback. */
function _extractFilePath(input: string): string | undefined {
  try {
    const parsed = JSON.parse(input);
    return parsed.path || parsed.filePath || parsed.file;
  } catch {
    // Plain text — try to extract a path-like string
    const match = input.match(/(?:[\w.-]+\/)+[\w.-]+\.\w{1,5}/);
    return match?.[0];
  }
}

/** Extract file path + line range from a read tool's input. */
function extractReadRange(input: string): ReadRange | undefined {
  try {
    const parsed = JSON.parse(input);
    const path = parsed.path || parsed.filePath || parsed.file;
    if (!path) return undefined;
    const offset =
      typeof parsed.offset === "number" ? parsed.offset : undefined;
    const limit = typeof parsed.limit === "number" ? parsed.limit : undefined;
    return { path, offset, limit };
  } catch {
    const match = input.match(/(?:[\w.-]+\/)+[\w.-]+\.\w{1,5}/);
    if (!match) return undefined;
    return { path: match[0], offset: undefined, limit: undefined };
  }
}

/**
 * Does `later` cover the line range of `earlier`?
 *
 * Coverage rules:
 * - Full-file read (no offset/limit) covers everything for the same path.
 * - A ranged read covers another ranged read when its [offset, offset+limit)
 *   interval is a superset of (or equal to) the other's interval.
 * - A ranged read does NOT cover a full-file read.
 */
export function laterReadCovers(later: ReadRange, earlier: ReadRange): boolean {
  if (later.path !== earlier.path) return false;

  // Full-file read covers everything for the same path.
  if (later.offset === undefined && later.limit === undefined) return true;

  // Later is a ranged read but earlier is full-file — can't cover.
  if (earlier.offset === undefined && earlier.limit === undefined) return false;

  // Both have ranges. Compute intervals.
  const laterStart = later.offset ?? 1;
  const earlierStart = earlier.offset ?? 1;

  // An open-ended later read (no limit) covers if its start <= earlier start.
  if (later.limit === undefined) return laterStart <= earlierStart;

  // Earlier is open-ended but later isn't — later can't cover infinite range.
  if (earlier.limit === undefined) return false;

  // Both bounded: [start, start+limit) superset check.
  const laterEnd = laterStart + later.limit;
  const earlierEnd = earlierStart + earlier.limit;
  return laterStart <= earlierStart && laterEnd >= earlierEnd;
}

/** Format a range label for dedup annotations. */
function rangeLabel(range: ReadRange): string {
  if (range.offset !== undefined && range.limit !== undefined) {
    return ` lines ${range.offset}-${range.offset + range.limit - 1}`;
  }
  if (range.offset !== undefined) {
    return ` from line ${range.offset}`;
  }
  return "";
}

/** Annotation for deduplicated tool output — follows the toolStripAnnotation() pattern. */
function dedupAnnotation(
  toolName: string,
  filePath?: string,
  range?: ReadRange,
): string {
  if (filePath) {
    const rl = range ? rangeLabel(range) : "";
    return `[earlier read of ${filePath}${rl} — see latest read below for current content]`;
  }
  return `[duplicate output — same content as later ${toolName} in this session — use recall for details]`;
}

/**
 * Replace duplicate tool outputs with compact back-references, keeping only
 * the latest occurrence of each unique output. Reduces context token usage
 * without information loss — the model sees the most recent content intact.
 *
 * Deduplicates by:
 * 1. Exact content hash: identical tool outputs (same file read twice, same command output)
 * 2. Range-aware file reads: read_file/read outputs for the same path where a later
 *    read covers the same or wider line range (full-file covers everything; a ranged
 *    read only covers another ranged read when its interval is a superset).
 *
 * The current turn (from currentTurnIdx onward) is never touched — the model
 * needs full context for its active work. Tool parts are never removed entirely;
 * only state.output is replaced with a compact annotation.
 *
 * Returns the original array reference (not a copy) when no duplicates exist.
 */
export function deduplicateToolOutputs(
  messages: MessageWithParts[],
  currentTurnIdx: number,
  /**
   * Optional per-session memo of prior collapse decisions, keyed by
   * `"<messageID>:<partID>"` → wasCollapsed. Makes dedup STABLE across turns:
   * once a tool output has been sent (full or collapsed) it keeps that form, so
   * a newly-appended later duplicate can't retroactively flip an already-cached
   * earlier message and bust the prompt cache. Caller persists this map across
   * transform() calls (per session). When omitted, dedup is stateless (legacy
   * behavior — still correct, just not cross-turn-stable).
   */
  stableDecisions?: Map<string, boolean>,
): MessageWithParts[] {
  // Track latest occurrence: contentKey → latest message index
  const contentLatest = new Map<string, number>();

  // Track all read ranges per file path, ordered by message index (ascending).
  // Each entry records the range and the message index so the second pass can
  // check whether any later read covers the current read's range.
  const fileReads = new Map<
    string,
    Array<{ range: ReadRange; msgIdx: number }>
  >();

  // First pass: scan all messages (including current turn) to build tracking maps.
  for (let i = 0; i < messages.length; i++) {
    for (const part of messages[i].parts) {
      if (!isToolPart(part) || part.state.status !== "completed") continue;
      const output = part.state.output;
      if (!output || output.length < DEDUP_MIN_CHARS) continue;

      const key = `${part.tool}:${simpleHash(output)}`;
      contentLatest.set(key, i);

      // For read-type tools, record the full range info
      if (part.tool === "read_file" || part.tool === "read") {
        const inputStr =
          typeof part.state.input === "string"
            ? part.state.input
            : JSON.stringify(part.state.input);
        const range = extractReadRange(inputStr);
        if (range) {
          let entries = fileReads.get(range.path);
          if (!entries) {
            entries = [];
            fileReads.set(range.path, entries);
          }
          entries.push({ range, msgIdx: i });
        }
      }
    }
  }

  // Second pass: replace earlier occurrences (but never touch the current turn)
  let changed = false;
  const result = messages.map((msg, msgIdx) => {
    if (msgIdx >= currentTurnIdx) return msg; // sacred boundary

    let partsChanged = false;
    const parts = msg.parts.map((part) => {
      if (!isToolPart(part) || part.state.status !== "completed") return part;
      const output = part.state.output;
      if (!output || output.length < DEDUP_MIN_CHARS) return part;

      // Check exact-match dedup: is this the latest occurrence of this content?
      const contentKey = `${part.tool}:${simpleHash(output)}`;
      const isLatestContent = contentLatest.get(contentKey) === msgIdx;

      // Check range-aware file dedup for read tools: does any later read
      // of the same file cover this read's range?
      let readRange: ReadRange | undefined;
      let coveredByLater = false;
      if (part.tool === "read_file" || part.tool === "read") {
        const inputStr =
          typeof part.state.input === "string"
            ? part.state.input
            : JSON.stringify(part.state.input);
        readRange = extractReadRange(inputStr);
        if (readRange) {
          const entries = fileReads.get(readRange.path);
          if (entries) {
            // Check if any entry with a higher message index covers this range
            for (const entry of entries) {
              if (
                entry.msgIdx > msgIdx &&
                laterReadCovers(entry.range, readRange)
              ) {
                coveredByLater = true;
                break;
              }
            }
          }
        }
      }

      // Cross-turn stability: if we already decided this exact tool part's
      // fate on a prior turn, honor it verbatim. A part sent full stays full;
      // a part collapsed stays collapsed. This prevents a newly-appended later
      // duplicate from retroactively collapsing an already-cached earlier
      // message (which would change its bytes and bust the prompt cache).
      const decisionKey = stableDecisions
        ? `${msg.info.id}:${part.id}`
        : undefined;
      const priorCollapsed = decisionKey
        ? stableDecisions?.get(decisionKey)
        : undefined;

      // Fresh decision: keep if this is both the latest content AND not covered
      // by a later read. A prior decision (when present) overrides this.
      const freshKeep = isLatestContent && !coveredByLater;
      const collapse = priorCollapsed ?? !freshKeep;

      if (decisionKey && priorCollapsed === undefined) {
        // Record the first-seen decision so it sticks on later turns.
        stableDecisions?.set(decisionKey, collapse);
      }

      if (!collapse) return part;

      // This is a duplicate — replace with compact annotation.
      // Drop structured `blocks` — the content is being compressed away.
      partsChanged = true;
      return {
        ...part,
        state: {
          ...part.state,
          output: dedupAnnotation(part.tool, readRange?.path, readRange),
          blocks: undefined,
        },
      };
    });

    if (!partsChanged) return msg;
    changed = true;
    return { ...msg, parts };
  });

  return changed ? result : messages;
}

// Ensure every tool part in the window has a terminal state (completed or error).
// Pending/running tool parts produce tool_use blocks at the API level but have no
// output to generate a matching tool_result — causing Anthropic to reject the request
// with "tool_use ids were found without tool_result blocks immediately after".
// This happens when a session errors mid-tool-execution (e.g. context overflow) and
// the tool part remains in pending/running state on the next transform.
// Converting to error state generates both tool_use + tool_result(is_error=true).
function sanitizeToolParts(messages: MessageWithParts[]): MessageWithParts[] {
  let changed = false;
  const result = messages.map((msg) => {
    if (msg.info.role !== "assistant") return msg;

    let partsChanged = false;
    const parts = msg.parts.map((part) => {
      if (!isToolPart(part)) return part;
      const { status } = part.state;
      if (status === "completed" || status === "error") return part;

      // pending or running → convert to error so SDK emits tool_result
      // Use a deterministic timestamp (0) instead of Date.now() so that
      // repeated transform() calls on the same stale pending part produce
      // identical bytes.  OpenCode's prompt-loop cache fix (e148f00aa)
      // preserves old pending parts across iterations; Date.now() here
      // would re-stamp them each call → different bytes → cache bust.
      partsChanged = true;
      const existingStart = "time" in part.state ? part.state.time.start : 0;
      return {
        ...part,
        state: {
          status: "error" as const,
          input: part.state.input,
          error: "[tool execution interrupted — session recovered]",
          metadata: "metadata" in part.state ? part.state.metadata : undefined,
          time: {
            start: existingStart,
            end: existingStart,
          },
        },
      };
    });

    if (!partsChanged) return msg;
    changed = true;
    return { ...msg, parts };
  });

  return changed ? result : messages;
}

function stripToolOutputs(parts: LorePart[]): LorePart[] {
  return parts.map((part) => {
    if (!isToolPart(part)) return part;
    if (part.state.status === "completed") {
      // Drop structured `blocks` — content is being compressed to annotation.
      return {
        ...part,
        state: {
          ...part.state,
          output: toolStripAnnotation(part.tool, part.state.output),
          blocks: undefined,
        },
      };
    }
    // Error outputs (e.g. large stack traces) must also be stripped under
    // aggressive (Layer 2) compression — otherwise failure-heavy turns evade
    // the strip entirely and can overflow the context.
    if (part.state.status === "error") {
      return {
        ...part,
        state: {
          ...part.state,
          error: toolStripAnnotation(part.tool, part.state.error),
          blocks: undefined,
        },
      };
    }
    return part;
  });
}

function _stripToTextOnly(parts: LorePart[]): LorePart[] {
  const stripped = parts
    .filter(isTextPart)
    .map((p) => ({
      ...p,
      text: normalize(stripSystemReminders(p.text)),
    }))
    .filter((p) => p.text.trim().length > 0) as LorePart[];
  // Guard against empty result — keep a placeholder so the message survives
  // toModelMessages and the conversation doesn't end with an assistant message.
  if (stripped.length === 0 && parts.length > 0) {
    const first = parts.find(isTextPart);
    if (first) return [{ ...first, text: "..." }];
  }
  return stripped;
}

// Build synthetic user/assistant message pair wrapping formatted distillation text.
// Shared by the cached and non-cached prefix paths.
function buildPrefixMessages(formatted: string): MessageWithParts[] {
  return [
    {
      info: {
        id: "lore-distilled-user",
        sessionID: "",
        role: "user" as const,
        time: { created: 0 },
        agent: "",
        model: { providerID: "", modelID: "" },
      },
      parts: [
        {
          id: "lore-distilled-user-part",
          sessionID: "",
          messageID: "lore-distilled-user",
          type: "text" as const,
          text: "[Memory context follows — do not reference this format in your responses]",
          time: { start: 0, end: 0 },
        },
      ],
    },
    {
      info: {
        id: "lore-distilled-assistant",
        sessionID: "",
        role: "assistant" as const,
        time: { created: 0 },
        parentID: "lore-distilled-user",
        modelID: "",
        providerID: "",
        mode: "memory",
        path: { cwd: "", root: "" },
        cost: 0,
        tokens: {
          input: 0,
          output: 0,
          reasoning: 0,
          cache: { read: 0, write: 0 },
        },
      },
      parts: [
        {
          id: "lore-distilled-assistant-part",
          sessionID: "",
          messageID: "lore-distilled-assistant",
          type: "text" as const,
          text: formatted,
          time: { start: 0, end: 0 },
        },
      ],
    },
  ];
}

// --- Importance-aware distillation selection ---
//
// When a compression stage limits distillation count (distLimit < Infinity),
// selects the most valuable distillations rather than blindly taking the last N.
// Scoring: 70% recency (position in chronological order) + 30% content signal.
// Results are re-sorted chronologically after selection so the prefix cache
// (Approach C) remains byte-stable when the same distillations are selected.
//
// Content signals (lightweight keyword detection, no LLM call):
//   - Decisions: "decision"/"decided"/"chose" → +0.3
//   - Gotchas/bugs: "gotcha"/"bug"/"fix"/"error" → +0.2
//   - Architecture: "architecture"/"pattern" → +0.1
//   - Meta-distilled (gen >= 1): +0.2 (consolidation = higher value density)

const DECISION_RE = /\b(?:decision|decided|chose|chosen|agreed)\b/i;
const GOTCHA_RE =
  /\b(?:gotcha|(?:critical|known|subtle)\s+bug|broken|crash(?:ed|es)?|regression)\b/i;
const ARCH_RE =
  /\b(?:architecture|design.(?:decision|pattern)|system.design)\b/i;

function importanceBonus(d: Distillation): number {
  let bonus = 0;
  if (DECISION_RE.test(d.observations)) bonus += 0.3;
  if (GOTCHA_RE.test(d.observations)) bonus += 0.2;
  if (ARCH_RE.test(d.observations)) bonus += 0.1;
  if (d.generation >= 1) bonus += 0.2;
  return Math.min(bonus, 1.0);
}

export function selectDistillations(
  all: Distillation[],
  limit: number,
): Distillation[] {
  if (all.length <= limit) return all;

  // Always include meta distillations (gen >= 1) — they contain the
  // consolidated session history and must not be evicted by recency-weighted
  // gen-0 segments. Without this guarantee, layer 3 (distLimit=5) would drop
  // the meta in favor of 5 recent gen-0 segments, losing older context. #417.
  const meta = all.filter((d) => d.generation >= 1);
  const gen0 = all.filter((d) => d.generation === 0);
  const remainingSlots = limit - meta.length;

  // If meta entries alone fill or exceed the limit, keep them all by score.
  if (remainingSlots <= 0) {
    const maxIdx = meta.length - 1;
    const scored = meta.map((d, i) => ({
      d,
      score: (maxIdx > 0 ? i / maxIdx : 1) * 0.7 + importanceBonus(d) * 0.3,
    }));
    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map((s) => s.d)
      .sort((a, b) => a.created_at - b.created_at);
  }

  // Fill remaining slots from gen-0 by recency + importance scoring.
  const maxIdx = gen0.length - 1;
  const scored = gen0.map((d, i) => ({
    d,
    score: (maxIdx > 0 ? i / maxIdx : 1) * 0.7 + importanceBonus(d) * 0.3,
  }));
  const topGen0 = scored
    .sort((a, b) => b.score - a.score)
    .slice(0, remainingSlots)
    .map((s) => s.d);

  // Merge and re-sort chronologically (cache-safe).
  return [...meta, ...topGen0].sort((a, b) => a.created_at - b.created_at);
}

// Build a synthetic message pair containing the distilled history.
// Non-cached path — used by layers 2+ which already cause full cache invalidation.
function distilledPrefix(distillations: Distillation[]): MessageWithParts[] {
  if (!distillations.length) return [];
  const formatted = formatDistillations(distillations);
  if (!formatted) return [];
  return buildPrefixMessages(formatted);
}

// --- Approach C: Append-only distillation prefix cache ---
//
// Caches the rendered prefix text per session. When new distillations arrive,
// only renders the new rows and appends them to the cached text. This keeps
// the prefix byte-identical between distillation runs, preserving the prompt
// cache. Only meta-distillation (which rewrites gen-0 rows into gen-1) causes
// a full re-render — and that happens roughly every 80-100 turns.

type PrefixCache = {
  /** The session this cache belongs to */
  sessionID: string;
  /** ID of the last distillation row included in the cached text */
  lastDistillationID: string;
  /** Number of rows that produced the cached text */
  rowCount: number;
  /** The rendered text (used to build delta appends) */
  cachedText: string;
  /** Ready-to-use message pair */
  prefixMessages: MessageWithParts[];
  /** Token estimate of prefixMessages */
  prefixTokens: number;
};

/**
 * Return the distilled prefix messages, reusing cached content when possible.
 * Uses per-session state from sessState.prefixCache (no module-level cache).
 *
 * Cache hit  — no new rows: returns the exact same prefixMessages object
 *              (byte-identical content, prompt cache preserved).
 * Warm hit   — returns the cached prefix as-is even when new gen-0 rows were
 *              appended in the DB. Re-rendering/appending those rows would
 *              mutate messages[1].content near the front of the prompt and
 *              bust the warm Anthropic prompt cache.
 * Full reset — first call, idle resume (onIdleResume clears prefixCache), or
 *              rows were rewritten by cold-boundary meta-distillation:
 *              renders everything from scratch and folds in accumulated rows.
 */
function distilledPrefixCached(
  distillations: Distillation[],
  sessionID: string,
  sessState: SessionState,
): { messages: MessageWithParts[]; tokens: number } {
  if (!distillations.length) {
    // Freeze the *absence* of a prefix too. If the first gen-0 distillation
    // arrives while the conversation cache is warm, injecting synthetic
    // messages[0/1] would be just as cache-busting as appending to an existing
    // prefix. onIdleResume clears this empty pin so a cold turn can render it.
    sessState.prefixCache = {
      sessionID,
      lastDistillationID: "",
      rowCount: 0,
      cachedText: "",
      prefixMessages: [],
      prefixTokens: 0,
    };
    return { messages: [], tokens: 0 };
  }

  const lastRow = distillations[distillations.length - 1];
  const prefixCache = sessState.prefixCache;

  // Cache is valid when: same session, row count only grew (no rewrites),
  // and the last previously-cached row still exists at the same position.
  const cacheValid =
    prefixCache !== null &&
    prefixCache.sessionID === sessionID &&
    prefixCache.rowCount <= distillations.length &&
    (prefixCache.rowCount === 0 ||
      distillations[prefixCache.rowCount - 1]?.id ===
        prefixCache.lastDistillationID);

  if (cacheValid) {
    // Warm-session freeze: keep messages[0/1] byte-identical. New gen-0
    // distillations accumulate in the DB but are not rendered into the prefix
    // until a cold boundary clears prefixCache (idle resume / test reset). This
    // trades slight memory staleness for prompt-cache stability; recall can
    // still retrieve the newly-distilled rows if needed.
    return {
      messages: prefixCache.prefixMessages,
      tokens: prefixCache.prefixTokens,
    };
  }

  // Full re-render: first call or meta-distillation rewrote rows
  const fullText = formatDistillations(distillations);
  if (!fullText) {
    sessState.prefixCache = null;
    return { messages: [], tokens: 0 };
  }

  const messages = buildPrefixMessages(fullText);
  const tokens = messages.reduce((sum, m) => sum + estimateMessage(m), 0);
  sessState.prefixCache = {
    sessionID,
    lastDistillationID: lastRow.id,
    rowCount: distillations.length,
    cachedText: fullText,
    prefixMessages: messages,
    prefixTokens: tokens,
  };
  return { messages, tokens };
}

/**
 * Serialize the cross-turn dedup decision memo for a session to a JSON string,
 * so the host can persist it (survives gateway restarts). Returns null when the
 * session has no recorded decisions.
 */
export function exportDedupDecisions(sessionID: string): string | null {
  const state = sessionStates.get(sessionID);
  if (!state || state.dedupDecisions.size === 0) return null;
  return JSON.stringify(Array.from(state.dedupDecisions.entries()));
}

/**
 * Restore a previously-persisted dedup decision memo into a session's state.
 * Ignores malformed input (the memo is an optimization — a bad blob just means
 * the first post-restart turn re-derives decisions). Does not overwrite an
 * already-populated in-memory memo.
 */
export function importDedupDecisions(sessionID: string, json: string): void {
  const state = getSessionState(sessionID);
  if (state.dedupDecisions.size > 0) return;
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return;
    for (const entry of parsed) {
      if (
        Array.isArray(entry) &&
        typeof entry[0] === "string" &&
        typeof entry[1] === "boolean"
      ) {
        state.dedupDecisions.set(entry[0], entry[1]);
      }
    }
  } catch {
    // Corrupt blob — start fresh.
  }
}

// For testing only — reset prefix cache state for a specific session (or all).
// Also clears the Bug 1 distilled-budget high-water (same lifecycle as the
// prefix — the high-water only makes sense when a prefix has been rendered).
export function resetPrefixCache(sessionID?: string) {
  if (sessionID) {
    const state = sessionStates.get(sessionID);
    if (state) {
      state.prefixCache = null;
      state.distilledBudgetHighWater = 0;
    }
  } else {
    for (const state of sessionStates.values()) {
      state.prefixCache = null;
      state.distilledBudgetHighWater = 0;
    }
  }
}

// For testing only — reset distillation snapshot for a specific session (or all).
// Also clears the Bug 1 distilled-budget high-water (prefix invalidated when the
// distillation set changes).
export function resetDistillationSnapshot(sessionID?: string) {
  if (sessionID) {
    const state = sessionStates.get(sessionID);
    if (state) {
      state.distillationSnapshot = null;
      state.distilledBudgetHighWater = 0;
    }
  } else {
    for (const state of sessionStates.values()) {
      state.distillationSnapshot = null;
      state.distilledBudgetHighWater = 0;
    }
  }
}

// --- Approach B: Lazy raw window eviction ---
//
// Tracks the ID of the first (oldest) message in the previous raw window.
// On the next turn, if the window starting at that message still fits within
// the raw budget, the cutoff is pinned — no messages are evicted and the raw
// window stays byte-identical for caching purposes. Only when the pinned
// window no longer fits (e.g. a large tool response pushed us over) is the
// cutoff allowed to advance forward by one message at a time.
//
// This eliminates the "window sliding on every turn" problem that was the
// dominant source of cache misses in gradient mode: each new turn appends a
// message to the conversation, but the start of the raw window only moves
// when it must.
//
// Reset conditions: session changes, or layer escalates to 2+ (the pinned
// window was too large even with stripping — something genuinely changed).
//
// When the pinned window overflows and we must rescan, we evict down to
// RAW_WINDOW_EVICT_TARGET of rawBudget rather than re-pinning right at the
// ceiling. A window re-pinned exactly at the budget ceiling overflows again
// after the next ~2 messages, degrading the pin into a per-turn sliding window
// that busts the prompt cache on every turn (the boundary marches forward ~2
// messages/turn). Evicting a chunk leaves headroom so the boundary stays stable
// for many turns between evictions, trading a little retained history for a
// large reduction in cache-write cost.
const RAW_WINDOW_EVICT_TARGET = 0.75;

type RawWindowCache = {
  sessionID: string;
  /** Number of raw messages (excluding prefix) in the pinned window at creation. */
  pinnedRawCount: number;
  /** Total number of messages in the input array when the pin was created.
   *  Used to compute how many new messages were appended since. */
  pinnedTotalCount: number;
  /** rawBudget that was in effect when the pin was created — used for the
   *  pin-validity check so that global budget fluctuations don't evict the pin. */
  pinnedBudget: number;
};

// For testing only — reset raw window cache state for a specific session (or all)
export function resetRawWindowCache(sessionID?: string) {
  if (sessionID) {
    const state = sessionStates.get(sessionID);
    if (state) {
      state.rawWindowCache = null;
      state.distilledBudgetHighWater = 0;
    }
  } else {
    for (const state of sessionStates.values()) {
      state.rawWindowCache = null;
      state.distilledBudgetHighWater = 0;
    }
  }
}

/**
 * Layer-1 tryFit with lazy eviction.
 * Uses per-session rawWindowCache from sessState (no module-level cache).
 *
 * Attempts to reuse the previous raw window cutoff before falling back to a
 * full backward scan. If the pinned window fits, returns it unchanged (same
 * message objects, byte-identical for prompt caching). If it doesn't fit,
 * delegates to the normal tryFit which finds the new minimal cutoff and
 * updates the cache.
 */
function tryFitStable(input: {
  messages: MessageWithParts[];
  prefix: MessageWithParts[];
  prefixTokens: number;
  distilledBudget: number;
  rawBudget: number;
  sessionID: string;
  sessState: SessionState;
}): Omit<
  TransformResult,
  "layer" | "usable" | "distilledBudget" | "rawBudget" | "refreshLtm"
> | null {
  // If the prefix already overflows its budget there's no point trying.
  if (input.prefixTokens > input.distilledBudget && input.prefix.length > 0)
    return null;

  const rawWindowCache = input.sessState.rawWindowCache;
  const cacheValid =
    rawWindowCache !== null && rawWindowCache.sessionID === input.sessionID;

  if (cacheValid && rawWindowCache) {
    // Compute the pinned index from the stored raw count + new message growth.
    // newMessages = messages appended since pin creation (typically 2 per turn).
    // The pinned window grows to include them: pinnedRawCount + newMessages.
    // This is resilient to front-trimming by the host (e.g. OpenCode evicting
    // old messages) because the offset is relative to the tail.
    const newMessages = Math.max(
      0,
      input.messages.length - rawWindowCache.pinnedTotalCount,
    );
    const windowSize = rawWindowCache.pinnedRawCount + newMessages;
    const pinnedIdx = Math.max(0, input.messages.length - windowSize);

    // Ensure the pinned window starts with a user message when a prefix is
    // present — the prefix ends with assistant so a leading assistant in the
    // raw window would create back-to-back assistants (#424).
    let adjustedPinnedIdx = pinnedIdx;
    if (input.prefix.length > 0) {
      while (
        adjustedPinnedIdx < input.messages.length &&
        input.messages[adjustedPinnedIdx].info.role === "assistant"
      ) {
        adjustedPinnedIdx++;
      }
    }

    // Measure the token cost of the pinned window.
    const pinnedWindow = input.messages.slice(adjustedPinnedIdx);
    const pinnedTokens = pinnedWindow.reduce(
      (sum, m) => sum + estimateMessage(m),
      0,
    );

    // Use the budget that was in effect when the pin was created with a 15%
    // hysteresis margin so that small budget fluctuations from overhead drift
    // and deduplicateToolOutputs() token-estimate changes don't evict the pin.
    // The high-water mark (max of pinned and current budgets) prevents overhead
    // EMA drift from shrinking the effective budget below what was valid when
    // the pin was created — the budget shrank due to overhead drift, not because
    // the context limit changed.
    const highWaterBudget = Math.max(
      rawWindowCache.pinnedBudget,
      input.rawBudget,
    );
    const effectiveBudget = highWaterBudget * 1.15;
    if (pinnedTokens <= effectiveBudget) {
      // Pinned window still fits within the hysteresis margin of the high-water
      // budget. Re-pin at the current budget when the old hysteresis is exceeded
      // so that next turn's check uses a fresh baseline.
      if (pinnedTokens > rawWindowCache.pinnedBudget * 1.15) {
        input.sessState.rawWindowCache = {
          ...rawWindowCache,
          pinnedRawCount: pinnedWindow.length,
          pinnedTotalCount: input.messages.length,
          pinnedBudget: input.rawBudget,
        };
      }
      // Apply system-reminder cleanup only (strip:"none" is the layer-1 mode),
      // returning the same message object references wherever nothing changed.
      const processed = pinnedWindow.map((msg) => {
        const parts = cleanParts(msg.parts);
        return parts !== msg.parts ? { info: msg.info, parts } : msg;
      });
      const total = input.prefixTokens + pinnedTokens;
      return {
        messages: [...input.prefix, ...processed],
        distilledTokens: input.prefixTokens,
        rawTokens: pinnedTokens,
        totalTokens: total,
        capFit: false,
      };
    }
    // Pinned window is too large for both budgets — fall through to rescan.
    log.info(
      `pin-overflow: session=${input.sessionID} pinnedTokens=${pinnedTokens} ` +
        `pinnedBudget=${rawWindowCache?.pinnedBudget} effectiveBudget=${Math.round(effectiveBudget)} ` +
        `currentRawBudget=${input.rawBudget} windowSize=${pinnedWindow.length}`,
    );
  }

  // Normal backward scan to find the tightest fitting cutoff. Evict down to a
  // chunk below the ceiling (rawFillBudget) so the re-pinned boundary has
  // headroom and won't overflow again on the next turn — preventing the
  // per-turn boundary march that busts the cache. The current-turn escalation
  // guard inside tryFit still uses the full rawBudget.
  //
  // Edge case: when the current turn alone is larger than rawFillBudget
  // (i.e. > 75% of rawBudget), the older-message fill collapses to ~0 and the
  // window keeps only the current turn — so a session whose every turn is that
  // large can still march. That's an extreme regime (the old full-budget rescan
  // also degraded, just at the 100% boundary); higher layers (tool stripping)
  // take over when even the current turn doesn't fit the full rawBudget.
  const result = tryFit({
    messages: input.messages,
    prefix: input.prefix,
    prefixTokens: input.prefixTokens,
    distilledBudget: input.distilledBudget,
    rawBudget: input.rawBudget,
    rawFillBudget: Math.floor(input.rawBudget * RAW_WINDOW_EVICT_TARGET),
    strip: "none",
  });

  if (result) {
    // Update the raw window cache: store the raw message count and total message
    // count so we can reconstruct the window position on the next turn even after
    // front-trimming by the host (e.g. OpenCode evicting old messages).
    // Snapshot the current rawBudget so future pin checks use the budget that
    // was in effect when this window was chosen (Option 1: snapshot isolation).
    const rawMessageCount = result.messages.length - input.prefix.length;
    if (rawMessageCount > 0) {
      input.sessState.rawWindowCache = {
        sessionID: input.sessionID,
        pinnedRawCount: rawMessageCount,
        pinnedTotalCount: input.messages.length,
        pinnedBudget: input.rawBudget,
      };
    }
  }

  return result;
}

export type SafetyLayer = 0 | 1 | 2 | 3 | 4;

// --- Compression stage table ---
// Defines the escalation path for layers 1-3. Each stage tries increasingly
// aggressive compression: tool stripping, tighter budgets, distillation trimming.
// Adding a new intermediate stage = one table entry.
type CompressionStage = {
  strip: "none" | "old-tools" | "all-tools";
  rawFrac: number | null; // fraction of usable; null = use default rawBudget
  distFrac: number | null; // fraction of usable; null = use default distilledBudget
  distLimit: number; // Infinity = all, 5 = last 5, etc.
  protectedTurns: number; // turns exempt from tool stripping
  useStableWindow: boolean; // use tryFitStable (Approach B pin cache)
};

const COMPRESSION_STAGES: CompressionStage[] = [
  {
    strip: "none",
    rawFrac: null,
    distFrac: null,
    distLimit: Infinity,
    protectedTurns: 0,
    useStableWindow: true,
  },
  {
    strip: "old-tools",
    rawFrac: 0.5,
    distFrac: null,
    distLimit: Infinity,
    protectedTurns: 2,
    useStableWindow: false,
  },
  {
    strip: "all-tools",
    rawFrac: 0.55,
    distFrac: 0.15,
    distLimit: 5,
    protectedTurns: 0,
    useStableWindow: false,
  },
];

export type TransformResult = {
  messages: MessageWithParts[];
  layer: SafetyLayer;
  distilledTokens: number;
  rawTokens: number;
  totalTokens: number;
  // Budget context (for display in context inspector)
  usable: number;
  distilledBudget: number;
  rawBudget: number;
  // Signals that the pipeline should re-run forSession() to refresh LTM
  // relevance scoring. Set on Layer 4 (emergency) where the context is
  // fully reset and mid-session knowledge may have changed relevance.
  refreshLtm: boolean;
  /** Number of consecutive cache busts detected for this session at the time
   *  of this transform. Surfaced so callers (and the bust-spiral detection in
   *  transform()) can observe the current bust pressure. */
  consecutiveBusts?: number;
  /** True when this transform took the cap-fit passthrough path (layer 0
   *  AND session fits the layer-0 cap with headroom AND fits under the hard
   *  ceiling). Drives the bust-spiral suppression for sessions whose busts
   *  come from structural causes (e.g. prompt-delta position drift after
   *  post-idle recompression), not genuine context growth (#952 follow-up
   *  to #797). Set only by the cap-fit passthrough return path; all other
   *  returns set this to false. */
  capFit: boolean;
};

// Per-session urgent distillation tracking.
// Keyed by sessionID. Set by layer returns in transformInner(),
// consumed (read + delete) by needsUrgentDistillation(sessionID).
const urgentDistillationMap = new Map<string, boolean>();
let urgentDistillationEnabled = true;

export function setUrgentDistillationEnabledForTest(enabled: boolean): void {
  urgentDistillationEnabled = enabled;
  if (!enabled) urgentDistillationMap.clear();
}

export function needsUrgentDistillation(sessionID: string): boolean {
  const v = urgentDistillationMap.get(sessionID) ?? false;
  urgentDistillationMap.delete(sessionID);
  return v;
}

/**
 * Layer-0 sizing for a given expected input. Single source of truth shared by
 * transformInner() (the layer decision) and isLargeColdStart() (the pipeline's
 * turn-1 LTM-injection decision) so the two never diverge. Reads the
 * module-global model limits set per-request by the host before transform().
 * (issue #796)
 */
function layer0Bounds(
  expectedInput: number,
  calibrated: boolean,
  sid: string | undefined,
): { layer0Input: number; layer0Ceiling: number; maxInput: number } {
  const maxInput = contextLimit - outputReserved;
  // chars/3 undercounts by ~1.63x on real sessions — without this an
  // uncalibrated session estimated at 146K passes Layer 0 but actually costs
  // 214K → overflow.
  const layer0Input = calibrated
    ? expectedInput
    : expectedInput * UNCALIBRATED_SAFETY;
  // Cost-aware cap: smaller of the API limit and the cost-derived cap
  // (0 = disabled → pure maxInput).
  let layer0Ceiling =
    maxLayer0Tokens > 0 ? Math.min(maxInput, maxLayer0Tokens) : maxInput;
  // Cold-cache awareness: the entire context is a cache WRITE on an
  // uncalibrated turn — tighten the cap to 70% to reduce cold-write cost.
  if (!calibrated && layer0Ceiling < maxInput) {
    layer0Ceiling = Math.floor(layer0Ceiling * 0.7);
  }
  // Free-write / non-caching: no expensive write to avoid → compress earlier.
  if (sid && isFreeWriteSession(sid)) {
    layer0Ceiling = Math.min(
      layer0Ceiling,
      Math.floor(maxInput * FREE_WRITE_LAYER0_FRACTION),
    );
  }
  return { layer0Input, layer0Ceiling, maxInput };
}

/**
 * Will an uncalibrated (first-sight) session of this size be forced to skip
 * Layer-0 passthrough and compress on its cold turn? Pure, read-only.
 *
 * Used by the gateway pipeline to decide whether to inject context-bound LTM
 * (system[2]) on turn 1 for an already-large resumed session instead of
 * deferring to turn 2. Returns false once the session is calibrated
 * (lastKnownInput > 0) — by then the normal per-turn logic applies.
 *
 * Shares layer0Bounds() with transformInner, so the caller can make the two
 * agree EXACTLY: pass `ltmTokens` = the LTM tokens that will be set for this
 * turn when system[2] is NOT injected (i.e. the stable/preference block only).
 * Then on a "not large" result the pipeline skips system[2] and calls
 * setLtmTokens(stableOnly), so transformInner's expectedInput equals the value
 * tested here — no decision-vs-compression drift band. (#796)
 */
export function isLargeColdStart(input: {
  messages: MessageWithParts[];
  sessionID?: string;
  /** Override the session's in-flight LTM token count (see docstring). */
  ltmTokens?: number;
  /**
   * Per-request model budget snapshot. When provided it is applied atomically
   * (synchronously, same as transform()) BEFORE the layer-0 computation, so the
   * module globals this predicate reads (contextLimit, outputReserved,
   * maxLayer0Tokens, qualityKnee) cannot be clobbered by a concurrent request
   * for a different model during the pipeline's intervening awaits between
   * setting the budget and calling this. Omit only in tests that set the
   * globals directly. (#1401)
   */
  budget?: ModelBudget;
}): boolean {
  if (input.budget) applyModelBudget(input.budget);
  const sid = input.sessionID ?? input.messages[0]?.info.sessionID;
  const sessState = sid ? getSessionState(sid) : makeSessionState();
  if (sessState.lastKnownInput > 0) return false;
  const overhead = getOverhead(sid);
  const sessLtmTokens =
    input.ltmTokens ?? (sid ? sessState.ltmTokens : ltmTokensFallback);
  const expectedInput =
    estimateMessages(input.messages) + overhead + sessLtmTokens;
  const { layer0Input, layer0Ceiling, maxInput } = layer0Bounds(
    expectedInput,
    false,
    sid,
  );
  // Compression is forced whenever the shared layer-0 passthrough predicate
  // fails — mirror transformInner exactly so the two never diverge (a divergence
  // makes the turn-1 LTM injection decision wrong; see #961 Seer 15319715).
  return !layer0Passes(layer0Input, layer0Ceiling, maxInput);
}

function transformInner(input: {
  messages: MessageWithParts[];
  projectPath: string;
  sessionID?: string;
}): TransformResult {
  const cfg = config();

  // --- Session state (must precede budget computation) ---
  const sid = input.sessionID ?? input.messages[0]?.info.sessionID;
  const sessState = sid ? getSessionState(sid) : makeSessionState();

  // Per-session overhead (Bug 1, lever 2): a session's own calibrated overhead,
  // not a global EMA blended across heterogeneous concurrent sessions.
  const overhead = getOverhead(sid);

  // Usable = full context minus output reservation minus fixed overhead (system + tools)
  // minus LTM tokens already injected into the system prompt this turn.
  // Read LTM tokens from per-session state to avoid cross-session contamination.
  //
  // NOTE: `usable` is sensitive to BOTH `contextLimit` (set per-turn from the
  // model spec, which can climb as models.dev data warms from a cold fallback)
  // and `overhead` (per-session EMA from calibrate() since Bug 1 lever 2 — see
  // `calibratedOverhead` for the now-fallback global). Anything derived as a
  // fraction of `usable` — e.g. distilled/raw budgets — therefore scales with a
  // large context window. Downstream economic decisions must not treat such a
  // budget as the real compressed size (see the tier gate's compressedEstimate
  // clamp below).
  // LTM is subtracted EXACTLY ONCE (sessLtmTokens here) — `calibrate` no longer
  // folds LTM into `overhead` (Bug 1, lever 3): documented intent was system
  // + tools only, but the previous code double-counted LTM via
  // actualInput − bodyEstimate (which INCLUDED LTM).
  const sessLtmTokens = sid ? sessState.ltmTokens : ltmTokensFallback;
  const usableRaw = Math.max(
    0,
    contextLimit - outputReserved - overhead - sessLtmTokens,
  );

  // No EMA-driven adaptive cap — use the full available context budget.
  // The layer-0 cap (maxLayer0Tokens) still applies for per-turn read cost,
  // and tier-based bust-vs-continue decisions control whether to compress
  // at quality boundaries.
  const usable = usableRaw;

  // `usable` is REAL provider tokens (contextLimit/overhead/ltm are all real,
  // and `overhead` is now TRUE system+tools — it no longer absorbs the chars/3
  // body undercount). The body budgets below are compared against chars/3 body
  // estimates (estimateMessage), so convert the real body space to the chars/3
  // scale by dividing by BODY_TOKEN_RATIO. This makes the distilled/raw windows
  // occupy at most `fraction × usable` REAL tokens — honoring cfg.budget.* — and
  // keeps the comparison apples-to-apples. (Overflow is guarded separately by
  // fitsWithSafetyMargin, which inflates back to real and adds overhead+ltm.)
  const bodyUsable = Math.floor(usable / BODY_TOKEN_RATIO);

  const distilledBudget = Math.floor(bodyUsable * cfg.budget.distilled);
  // Base raw budget. May be overridden below for post-idle compact mode.
  let rawBudget = Math.floor(bodyUsable * cfg.budget.raw);

  // --- Escalated-stage budget ceiling (layers 2-3 only) ---
  // The ESCALATED compression stages (layer 2: rawFrac 0.5; layer 3: rawFrac
  // 0.55) size their raw/distilled windows as a fraction of `usable`. On a
  // high-context model (e.g. a 1M-token opus → usable ~800K) `usable * 0.5` ≈
  // 400K — far LARGER than the ~200K cost cap (maxLayer0Tokens) whose breach
  // triggered compression, AND larger than the layer-1 window itself. So
  // escalating to a "more aggressive" layer paradoxically GREW the context,
  // unbounded, until the model's real limit overflowed. (This wedged session
  // 0AVWKugtmhBKqLOX9 at layer 2: layer-1 200K → layer-2 356K → 461K, then
  // "prompt is too long".) Clamp the ESCALATED-stage budgets to the layer-0
  // cost ceiling so each higher layer actually shrinks the window toward the
  // cap. NOTE: the layer-1 stable window (stage 0, rawFrac=null) deliberately
  // keeps the full `usable * raw` budget — the cost cap governs WHEN to
  // compress, not the layer-1 window size, and shrinking it would defeat the
  // cache-stable raw-window pin (its chunked eviction needs headroom).
  //   - maxLayer0Tokens === 0 (cost cap disabled): stageBudgetUsable === usable,
  //     a no-op preserving the explicit "use the model's full context" opt-out.
  //   - small-context models: usable < ceiling, so this is also a no-op.
  const stageBudgetCeiling =
    maxLayer0Tokens > 0
      ? Math.min(contextLimit - outputReserved, maxLayer0Tokens)
      : contextLimit - outputReserved;
  const stageBudgetUsable = Math.min(usable, stageBudgetCeiling);

  // --- Force escalation (reactive error recovery) ---
  // When the API previously rejected with "prompt is too long", skip layers
  // below the forced minimum to ensure enough trimming on the next attempt.
  // One-shot: consumed here and reset to 0 (both in-memory and on disk).
  let effectiveMinLayer = sessState.forceMinLayer;
  sessState.forceMinLayer = 0;
  if (sid && effectiveMinLayer > 0) saveForceMinLayer(sid, 0);

  // --- Approach A: Cache-preserving passthrough ---
  // Use exact token count from the previous API response when available.
  // Only the delta (messages added since last call) uses chars/3 estimation,
  // making the layer-0 decision highly accurate from the API's own tokenizer.
  // maxInput = absolute ceiling the API enforces: input_tokens + max_tokens <= context
  const maxInput = contextLimit - outputReserved;

  // True when we have real API token data from a previous turn in this session.
  // When false (first turn / session change), chars/3 estimates may still diverge
  // from the real tokenizer — so tryFit output must be validated with a safety
  // multiplier before being used.
  const calibrated = sessState.lastKnownInput > 0;

  // Returns true if a rebuilt compression-stage window is safe to ship.
  //
  // A rebuilt window's `totalTokens` is a FRESH chars/3 estimate of the WHOLE
  // window — it is NOT anchored to the API's last real count the way the
  // layer-0 delta path (expectedInput = lastKnownInput + delta) is. So being
  // "calibrated" does NOT make a rebuilt window exact: chars/3 undercounts the
  // real tokenizer ~1.5-3x. Previously this returned `true` unconditionally for
  // calibrated sessions, so a stage whose real size exceeded the model's
  // context window was shipped anyway → unrecoverable "prompt is too long"
  // (this, with the unclamped budgets, wedged session 0AVWKugtmhBKqLOX9).
  //
  // Validate every rebuilt window — calibrated or not — against the hard API
  // ceiling with the same undercount safety multiplier. Rejecting an
  // over-ceiling stage makes the loop escalate to a tighter layer (ultimately
  // the always-returns emergency tail) instead of overflowing the model. This
  // is only the stage-loop acceptance gate (the layer-0 / tier-gate paths use
  // their own `maxInput * HARD_CEILING_MARGIN` checks against expectedInput).
  //
  // Scale-correct check: inflate the chars/3 body (result.totalTokens) back to
  // REAL tokens via BODY_TOKEN_RATIO and add the real overhead + LTM (which are
  // NOT in result.totalTokens — it is the message body only). Compare to
  // maxInput. This is exact: it is the same quantity the API counts. Body
  // budgets are now sized as fractions of `usable` REAL tokens (bodyUsable =
  // usable/RATIO chars/3), so the layer-1 window real-total is
  // 0.65*(maxInput−overhead−ltm) + overhead + ltm = 0.65*maxInput +
  // 0.35*(overhead+ltm) < maxInput — never falsely rejected in the normal
  // regime, even with the 1.15x stable-window hysteresis (~0.71). The old check
  // (`totalTokens * 1.5 <= maxInput`) ignored overhead+ltm; with the bigger
  // post-fix budgets it would have under-counted and risked shipping an
  // over-ceiling window.
  function fitsWithSafetyMargin(
    result: { totalTokens: number } | null,
  ): boolean {
    if (!result) return false;
    return (
      result.totalTokens * BODY_TOKEN_RATIO + overhead + sessLtmTokens <=
      maxInput
    );
  }

  // --- Monotonic compression-layer ratchet ---
  // Once a session compresses to layer L (1-3), NEVER select a lower layer for
  // the rest of the session. Every downgrade re-renders the distilled prefix at
  // messages[0/1] with a different budget/strip level and front-busts the whole
  // prompt cache; the next turn re-compresses and busts again (0<->N / 2<->1
  // thrash). Reclaiming window by decompressing is a false economy — the reclaim
  // is itself a full cache write.
  //
  // This is unconditional: it does NOT release when the host shrinks the
  // conversation ("genuine compaction"). The earlier design released the floor on
  // a shrink, but decompressing to exploit the reclaimed room just trades a
  // cache-read-cheap compressed prefix for a full re-write — the opposite of what
  // we want. A truly fresh conversation is a new session (new cache) anyway.
  //
  // `maxSustainedLayer` only ever holds 1-3 (see the update site after
  // transform); layer 4 (emergency overflow) is excluded, so a one-shot overflow
  // bust never latches the session at emergency — after recovery it settles back
  // to this sustained floor. `forceMinLayer` (the emergency escalation) is
  // applied separately via the initial `effectiveMinLayer` and can still push
  // ABOVE this floor for the recovery turn.
  if (sessState.maxSustainedLayer >= 1) {
    effectiveMinLayer = Math.max(
      effectiveMinLayer,
      sessState.maxSustainedLayer,
    ) as SafetyLayer;
  }

  // --- Post-idle compact layer ---
  // When the cache just went cold (onIdleResume fired), skip layer 0 full-raw
  // passthrough and use a tighter raw budget. Rationale: the entire context is
  // a cache WRITE regardless — a smaller total costs less to write, and
  // aggressive idle distillation already captured older history in the prefix.
  // The flag is one-shot: consumed here and reset so subsequent turns use
  // normal budgets once the cache is warm.
  const postIdleCompact = sessState.postIdleCompact;
  if (postIdleCompact) {
    sessState.postIdleCompact = false;
    // Skip layer 0 — don't pass through all raw messages on a cold cache.
    effectiveMinLayer = Math.max(effectiveMinLayer, 1) as SafetyLayer;
    // Use a tighter raw budget on cold cache to limit write cost. chars/3 scale
    // (bodyUsable) to match the body estimates, same as the base rawBudget.
    rawBudget = Math.floor(bodyUsable * 0.2);
    log.info(
      `post-idle compact: session=${sid} rawBudget=${rawBudget}` +
        ` (${Math.floor(bodyUsable * cfg.budget.raw)}→${rawBudget})`,
    );
  }

  let expectedInput: number;
  if (calibrated) {
    // Exact approach: prior API count + estimate of only genuinely new messages.
    // Use message ID tracking (Option B) to identify new messages accurately.
    // After compression, the "last window" is a subset of the full message array —
    // counting by index would treat evicted messages as new (off-by-250 error).
    // Safety multiplier for the delta portion of the calibrated estimate.
    // The base (lastKnownInput) is exact from the API, but new messages use
    // chars/3 which undercounts by ~1.68x.  Calibrated overhead captures most
    // of the structural gap, but the per-message content gap remains ~20-30%.
    // 1.3 covers this without triggering unnecessary compression.
    const CALIBRATED_DELTA_SAFETY = 1.3;

    const newMessages =
      sessState.lastWindowMessageIDs.size > 0
        ? input.messages.filter(
            (m) => !sessState.lastWindowMessageIDs.has(m.info.id),
          )
        : input.messages.slice(
            -Math.max(
              0,
              input.messages.length - sessState.lastKnownMessageCount,
            ),
          );
    const rawNewMsgTokens = newMessages.reduce(
      (s, m) => s + estimateMessage(m),
      0,
    );
    const newMsgTokens = Math.ceil(rawNewMsgTokens * CALIBRATED_DELTA_SAFETY);
    const ltmDelta = sessLtmTokens - sessState.lastKnownLtm;
    expectedInput = sessState.lastKnownInput + newMsgTokens + ltmDelta;
  } else {
    // First turn or session change: fall back to chars/3 estimate + overhead.
    const messageTokens = input.messages.reduce(
      (s, m) => s + estimateMessage(m),
      0,
    );
    expectedInput = messageTokens + overhead + sessLtmTokens;
  }

  // Layer-0 sizing (input estimate + ceiling). Extracted to layer0Bounds() so
  // the pipeline's turn-1 LTM decision (isLargeColdStart) shares ONE definition
  // with the layer choice here — a disagreement would re-introduce a bust. (#796)
  const { layer0Input, layer0Ceiling } = layer0Bounds(
    expectedInput,
    calibrated,
    sid,
  );

  // Record the gradient's own full-body size estimate so the shared cache-
  // economics evaluator (and therefore the warmer) read identical sizes from one
  // place. `cacheSizeCompressed` is set AUTHORITATIVELY from the actual rebuilt
  // window at the single transform() exit (issue #881) — see transform() — so it
  // is correct on EVERY layer path (including layer >= 1 sessions held by the
  // sticky/prefix-floor/post-idle/first-sight-large guards, which bypass the tier
  // gate below). Seed it to full here as the no-compaction default; the exit
  // overwrites it with the real compressed size whenever result.layer >= 1.
  if (sid) {
    sessState.cacheSizeFull = Math.max(0, Math.round(layer0Input));
    sessState.cacheSizeCompressed = sessState.cacheSizeFull;
    // Capture the inputs transform() needs to lift the compressed size onto the
    // same INPUT scale as cacheSizeFull (issue #886): the non-message floor
    // (overhead + LTM, which compaction never removes) and the safety factor
    // layer0Bounds applied to cacheSizeFull (1 when calibrated, else
    // UNCALIBRATED_SAFETY). Stored un-inflated; transform() inflates both the
    // floor and the rebuilt-window body together via computeCompressedCacheSize.
    sessState.cacheNonBodyTokens = Math.max(0, overhead + sessLtmTokens);
    sessState.cacheBodySafety = calibrated ? 1 : UNCALIBRATED_SAFETY;
  }

  // First-sight large session → skip the Layer-0 cold full-write. An
  // uncalibrated session already over the Layer-0 ceiling is a resumed
  // conversation Lore hasn't tracked in-process. Passing it through at Layer 0
  // writes the entire raw body on a cold cache; the next (calibrated) turn then
  // re-windows to Layer 1 (a second full write), and LTM injection adds a third
  // (system[2]) bust. Compressing now collapses all of that into the single
  // unavoidable cold write. Mirrors postIdleCompact's "cache write regardless"
  // rationale: the tier gate's continue-at-Layer-0 economics assume a WARM
  // cache, which does not exist on a cold turn. (issue #796)
  if (!calibrated && layer0Input > layer0Ceiling) {
    effectiveMinLayer = Math.max(effectiveMinLayer, 1) as SafetyLayer;
  }

  if (
    effectiveMinLayer === 0 &&
    layer0Passes(layer0Input, layer0Ceiling, maxInput)
  ) {
    // All messages fit — return unmodified to preserve append-only prompt-cache pattern.
    // Raw messages are strictly better context than lossy distilled summaries.
    // The quality-hard-ceiling clause (fill > QUALITY_HARD_CEILING_FRACTION of
    // the window) blocks pass-through when it is the binding ceiling: l0cap
    // disabled (no pricing / targetCacheRead=0), a free-write session
    // (~0.65×maxInput), OR the cheap-cache priced path (cost cap clamped to the
    // window → layer0Ceiling ≈ maxInput ≥ 0.8×window). There a large window
    // would otherwise pass through at layer 0 well past the degraded boundary.
    // Past that fill the clause forces compression: the window fails the
    // tier-gate condition and drops into the compression-stages loop directly —
    // a fill-relative cliff that deliberately bypasses shouldCompress (and its
    // free-write churn guard). When a tighter cost-derived layer0Ceiling binds
    // first (expensive-cache / small target$) this clause is inert and the
    // graduated multiplier governs instead. Re-compression cannot loop: after
    // one compression layer0Input drops below 0.8 fill. (#961)
    const messageTokens = calibrated
      ? expectedInput - (sessLtmTokens - sessState.lastKnownLtm) // approximate raw portion
      : expectedInput - overhead - sessLtmTokens;
    return {
      messages: input.messages,
      layer: 0,
      distilledTokens: 0,
      rawTokens: Math.max(0, messageTokens),
      totalTokens: Math.max(0, messageTokens),
      usable,
      distilledBudget,
      rawBudget,
      refreshLtm: false,
      capFit: true,
    };
  }

  // --- Tier-based bust-vs-continue gate ---
  // When expectedInput exceeds the layer-0 cap but still fits in the model's
  // context window, check whether compression is economically justified.
  // If not (bust cost ≥ 85% of continue cost), skip compression and pass
  // through at layer 0 — the cache reads are cheap enough to justify the
  // larger context, and raw messages are better quality than distilled.
  //
  // The quality penalty is applied INSIDE shouldCompress (qualityCostMultiplier
  // inflates the continue cost as the window grows), so this gate needs no
  // fixed ceiling: shouldCompress returns true progressively earlier past the
  // tier-1 boundary and always eventually, pulling a large window back down. The
  // absolute maxInput*HARD_CEILING_MARGIN clause remains as the last-resort stop.
  if (
    effectiveMinLayer === 0 &&
    layer0Input > layer0Ceiling &&
    layer0Input <= maxInput * HARD_CEILING_MARGIN &&
    sid
  ) {
    const busts = getSessionState(sid).consecutiveBusts;
    const freeWrite = isFreeWriteSession(sid);
    // For compression, estimate the compressed size as the layer-1 budget
    // (distilled + raw fractions). This is a rough upper bound — actual
    // compressed output may be smaller.
    //
    // Clamp to layer0Ceiling (the cost-aware layer-0 cap, ~l0cap): the raw
    // distilled+raw budget is scaled off `usable`, which for high-context
    // models (e.g. a 1M-token opus → usable ~957K) inflates the estimate to
    // ~0.65*usable (~620K) — far larger than what compression actually yields
    // (it targets layer0Ceiling, ~200K). Feeding the inflated figure to
    // shouldCompress() makes bustCost dwarf continueCost, so the gate refuses
    // to compress every turn — even under sustained-bust write-rate repricing —
    // and the raw context grows unbounded until the hard ceiling. Clamping to
    // the real compression target makes the economics reflect reality.
    const compressedEstimate = Math.min(
      distilledBudget + rawBudget,
      layer0Ceiling,
    );
    // NOTE: `cacheSizeCompressed` is NOT set here. It used to be refined to
    // `compressedEstimate` at this point, but (a) when shouldCompress() below
    // returns false the session stays at layer 0 with NO compaction, so leaving
    // compressed < full wrongly implied savings, and (b) it covered only this
    // layer-0 gate. transform() now sets it authoritatively from the actual
    // rebuilt window on every layer path (issue #881). `compressedEstimate` is
    // still the input to shouldCompress() (the bust-vs-continue decision).
    if (
      !shouldCompress(Math.round(layer0Input), compressedEstimate, busts, {
        freeWrite,
      })
    ) {
      const messageTokens = calibrated
        ? expectedInput - (sessLtmTokens - sessState.lastKnownLtm)
        : expectedInput - overhead - sessLtmTokens;
      log.info(
        `tier gate: session=${sid} skipping compression — bustCost not justified` +
          ` (input=${Math.round(layer0Input)} compressed=${compressedEstimate} busts=${busts})`,
      );
      return {
        messages: input.messages,
        layer: 0,
        distilledTokens: 0,
        rawTokens: Math.max(0, messageTokens),
        totalTokens: Math.max(0, messageTokens),
        usable,
        distilledBudget,
        rawBudget,
        refreshLtm: false,
        consecutiveBusts: busts,
        capFit: false,
      };
    }
  }

  // --- Gradient mode: context exhausted (or force-escalated), compress older messages ---

  // Pre-pass: deduplicate repeated tool outputs before layer selection.
  // Keeps only the latest occurrence of each unique output, replacing earlier
  // ones with compact annotations. This can save thousands of tokens for sessions
  // with repeated file reads, potentially avoiding escalation to higher layers.
  const turnStart = currentTurnStart(input.messages);
  // Pass the per-session decision memo so dedup stays byte-stable across turns
  // (an already-sent output keeps its full/collapsed form). Only when we have a
  // session to scope the memo to.
  const dedupMessages = deduplicateToolOutputs(
    input.messages,
    turnStart,
    sid ? sessState.dedupDecisions : undefined,
  );

  const distillations = sid
    ? loadDistillationsCached(input.projectPath, sid, input.messages, sessState)
    : [];

  // Layer 1 uses the append-only cached prefix (Approach C) to keep the
  // distilled content byte-identical between distillation runs, preserving
  // the prompt cache. Layers 2+ already cause full cache invalidation via
  // tool stripping / message restructuring, so they use the non-cached path.
  const cached = sid
    ? distilledPrefixCached(distillations, sid, sessState)
    : (() => {
        const msgs = distilledPrefix(distillations);
        return {
          messages: msgs,
          tokens: msgs.reduce((sum, m) => sum + estimateMessage(m), 0),
        };
      })();

  // --- Compression stages (layers 1-3) ---
  // Data-driven table replaces three hardcoded layer blocks. Each stage
  // escalates tool stripping and/or tightens distillation budgets.
  // Stage 0 (layer 1): stable window (Approach B), no stripping
  // Stage 1 (layer 2): strip old tool outputs, protect last 2 turns
  // Stage 2 (layer 3): strip ALL tool outputs, keep only 5 distillations
  for (let s = 0; s < COMPRESSION_STAGES.length; s++) {
    const stageLayer = (s + 1) as SafetyLayer;
    if (effectiveMinLayer > stageLayer) continue;

    const stage = COMPRESSION_STAGES[s];
    // Budget scaling by layer:
    //   - Layer 1 (stage 0): keep the FULL unclamped base budgets (usable*raw,
    //     usable*distilled). The cost cap governs WHEN to compress, not the
    //     layer-1 window size; clamping it would starve the cache-stable
    //     raw-window pin (its chunked eviction needs headroom).
    //   - Escalated layers (2-3): clamp BOTH raw AND distilled to the layer-0
    //     cost ceiling (`stageBudgetUsable`). On a 1M-token model `usable*0.5`
    //     ≈ 400K — far LARGER than the ~200K cap that triggered compression AND
    //     larger than the layer-1 window — so escalating GREW the context until
    //     the model overflowed (wedged session 0AVWKugtmhBKqLOX9). Both
    //     dimensions must be clamped: clamping only raw still lets the distilled
    //     prefix balloon to usable*0.25 (~242K on 1M), so the total would not
    //     actually shrink toward the cap. A null fraction on an escalated stage
    //     (e.g. layer 2's distFrac) falls back to the configured base fraction,
    //     still clamped to the ceiling.
    // `stageBudgetUsable` is REAL tokens (min of real usable and the real cost
    // ceiling); divide by BODY_TOKEN_RATIO so the escalated body budgets land on
    // the same chars/3 scale as the body estimates (matching distilledBudget/
    // rawBudget above).
    const isEscalated = stageLayer > 1;
    const stageRawBudget = isEscalated
      ? Math.floor(
          (stageBudgetUsable * (stage.rawFrac ?? cfg.budget.raw)) /
            BODY_TOKEN_RATIO,
        )
      : rawBudget;
    const stageDistBudget = isEscalated
      ? Math.floor(
          (stageBudgetUsable * (stage.distFrac ?? cfg.budget.distilled)) /
            BODY_TOKEN_RATIO,
        )
      : distilledBudget;

    // Determine prefix: if distLimit is finite, re-render with trimmed distillations.
    // Otherwise use the cached prefix (Approach C, byte-identical for cache).
    let stagePrefix = cached.messages;
    let stagePrefixTokens = cached.tokens;
    let stageDistillations = distillations;
    if (
      stage.distLimit !== Infinity &&
      distillations.length > stage.distLimit
    ) {
      stageDistillations = selectDistillations(distillations, stage.distLimit);
      stagePrefix = distilledPrefix(stageDistillations);
      stagePrefixTokens = stagePrefix.reduce(
        (sum, m) => sum + estimateMessage(m),
        0,
      );
    }

    // Distilled-prefix budget hysteresis (Bug 1, lever 1). On the layer-1 stable
    // stage, gate the trim against the high-water budget, not the raw per-turn
    // budget. Residual overhead-EMA wobble (even per-session) nudges
    // stageDistBudget a few K each turn; near the prefix size that flip-flops the
    // trim and front-busts messages[0]. The high-water only ratchets UP, so once
    // the prefix is admitted it stays byte-stable until the prefix GENUINELY
    // outgrows the best budget ever seen. Escalated stages (2-3) keep the raw
    // stage budget — they intentionally clamp toward the cost ceiling.
    const useHighWater = stage.useStableWindow && !!sid;
    const trimBudget = useHighWater
      ? effectiveDistilledBudget(
          stageDistBudget,
          sessState.distilledBudgetHighWater,
        )
      : stageDistBudget;

    // Budget-aware prefix trim: if the rendered prefix still exceeds this
    // stage's (effective) distilled budget, drop gen-0 distillations
    // (selectDistillations always preserves meta/gen>=1) until it fits — instead
    // of handing tryFit an over-budget prefix, which returns null and escalates
    // the layer. A meta-distillation rewrite forces a full re-render
    // (cacheValid=false) whose size can blow past every stage's budget, falling
    // through to emergency Layer 4 even when the session has ample headroom (the
    // distilled-prefix front-bust). Trimming keeps the session at a compressed
    // layer (1-3) with the prefix PRESENT, so messages[0] stays byte-stable.
    // NOTE: on a STEADY warm turn the cached prefix already fits, so this block
    // does not fire and messages[0/1] stay byte-identical. With the high-water
    // gate it ALSO does not fire on minor budget contraction (the Bug 1 fix).
    // Edge case: if even meta (gen>=1) entries alone exceed the budget, the
    // search converges to an empty prefix (dropping meta) rather than escalating.
    if (stagePrefixTokens > trimBudget && stageDistillations.length > 0) {
      let lo = 0;
      let hi = stageDistillations.length;
      // Largest count whose rendered prefix fits trimBudget (binary search).
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        const candidate = selectDistillations(stageDistillations, mid);
        const tokens = distilledPrefix(candidate).reduce(
          (sum, m) => sum + estimateMessage(m),
          0,
        );
        if (tokens <= trimBudget) lo = mid;
        else hi = mid - 1;
      }
      const fitted = selectDistillations(stageDistillations, lo);
      stagePrefix = distilledPrefix(fitted);
      stagePrefixTokens = stagePrefix.reduce(
        (sum, m) => sum + estimateMessage(m),
        0,
      );
    }

    // Stage 0 (layer 1) uses tryFitStable for Approach B pin cache.
    // Higher stages reset the raw window cache and use plain tryFit.
    let result: Omit<
      TransformResult,
      "layer" | "usable" | "distilledBudget" | "rawBudget" | "refreshLtm"
    > | null;
    if (stage.useStableWindow && sid) {
      // Use the high-water (trimBudget) so the stable stage admits the same
      // prefix the trim gate kept — otherwise tryFitStable's own
      // `prefixTokens > distilledBudget` guard would reject it and escalate.
      result = tryFitStable({
        messages: dedupMessages,
        prefix: stagePrefix,
        prefixTokens: stagePrefixTokens,
        distilledBudget: trimBudget,
        rawBudget: stageRawBudget,
        sessionID: sid,
        sessState,
      });
    } else {
      // Reset raw window cache when leaving stage 0 — higher stages use full
      // scans and already break the prompt cache. Must fire even when stage 1
      // is skipped via effectiveMinLayer (e.g. forceMinLayer = 3).
      // trimBudget === stageDistBudget here (useHighWater is false on escalated
      // stages), so the high-water never relaxes an escalated stage's clamp.
      sessState.rawWindowCache = null;
      sessState.distilledBudgetHighWater = 0;
      result = tryFit({
        messages: dedupMessages,
        prefix: stagePrefix,
        prefixTokens: stagePrefixTokens,
        distilledBudget: trimBudget,
        rawBudget: stageRawBudget,
        strip: stage.strip,
        protectedTurns: stage.protectedTurns,
      });
    }

    if (result && fitsWithSafetyMargin(result)) {
      // Ratchet the distilled-budget high-water on a successful layer-1 fit
      // (Bug 1, lever 1). It only ever increases, so a later turn whose budget
      // dipped (overhead-EMA wobble) still admits the already-rendered prefix
      // without a re-trim. Reset on escalation/idle (alongside rawWindowCache).
      if (useHighWater) {
        sessState.distilledBudgetHighWater = Math.max(
          sessState.distilledBudgetHighWater,
          stageDistBudget,
        );
      }
      // Trigger urgent distillation when: (a) higher stages always need it, or
      // (b) stage 0 with no distillations = first time in gradient mode.
      if (urgentDistillationEnabled && sid && (s > 0 || cached.tokens === 0)) {
        urgentDistillationMap.set(sid, true);
      }
      return {
        ...result,
        layer: stageLayer,
        usable,
        distilledBudget,
        rawBudget,
        refreshLtm: false,
        consecutiveBusts: sid ? getSessionState(sid).consecutiveBusts : 0,
        capFit: false,
      };
    }
  }

  // All compression stages exhausted — reset raw window cache before emergency.
  sessState.rawWindowCache = null;
  sessState.distilledBudgetHighWater = 0;

  // Layer 4: Emergency — last 2 distillations + token-budget raw tail.
  // We do NOT strip tool parts here: doing so would cause an infinite tool-call loop because
  // the model would lose sight of its own in-progress tool calls and re-invoke them endlessly.
  // Instead, we aggressively drop old messages and rely on the `recall` tool (which the model
  // is always instructed to use) to retrieve any older details it needs.
  //
  // Token-budget tail (F7): instead of a fixed `slice(-3)`, size the raw
  // tail using `clamp(usable * 0.25, 2_000, 8_000)` tokens — matching
  // upstream OpenCode's tail-budget formula for compaction. The current
  // agentic turn (from `currentTurnStart()`) is ALWAYS fully included even
  // if it alone exceeds the tail budget — layer 4 is the terminal layer
  // and must always return. Remaining budget is filled backward with older
  // messages.
  if (urgentDistillationEnabled && sid) urgentDistillationMap.set(sid, true);
  const nuclearDistillations = selectDistillations(distillations, 2);
  const nuclearPrefix = distilledPrefix(nuclearDistillations);
  const nuclearPrefixTokens = nuclearPrefix.reduce(
    (sum, m) => sum + estimateMessage(m),
    0,
  );

  // Token budget for the raw tail. clamp(bodyUsable * 0.25, 2K, 8K) — chars/3
  // scale (bodyUsable) to match estimateMessage; the 2K/8K clamp dominates
  // except on very small contexts.
  const tailBudget = Math.max(
    2_000,
    Math.min(8_000, Math.floor(bodyUsable * 0.25)),
  );

  // Current turn is always included (non-negotiable — dropping it causes
  // the infinite tool-call loop). Clean parts but never strip tool outputs.
  const nuclearTurnStart = currentTurnStart(input.messages);
  const currentTurn = input.messages.slice(nuclearTurnStart).map((m) => ({
    info: m.info,
    parts: cleanParts(m.parts),
  }));
  const currentTurnTokens = currentTurn.reduce(
    (sum, m) => sum + estimateMessage(m),
    0,
  );

  // Fill remaining budget walking backward from the turn boundary.
  const olderMessages: MessageWithParts[] = [];
  let olderTokens = 0;
  const remaining = Math.max(0, tailBudget - currentTurnTokens);
  for (let i = nuclearTurnStart - 1; i >= 0 && olderTokens < remaining; i--) {
    const msg = input.messages[i];
    const est = estimateMessage(msg);
    if (olderTokens + est > remaining) break;
    olderMessages.unshift({
      info: msg.info,
      parts: cleanParts(msg.parts),
    });
    olderTokens += est;
  }

  // Ensure role alternation at the prefix/raw boundary: drop leading assistant
  // messages from the older tail so the raw window starts with user (#424).
  while (
    olderMessages.length > 0 &&
    nuclearPrefix.length > 0 &&
    olderMessages[0].info.role === "assistant"
  ) {
    olderTokens -= estimateMessage(olderMessages[0]);
    olderMessages.shift();
  }

  const nuclearRaw = [...olderMessages, ...currentTurn];
  const nuclearRawTokens = olderTokens + currentTurnTokens;

  const busts = sid ? getSessionState(sid).consecutiveBusts : 0;

  return {
    messages: [...nuclearPrefix, ...nuclearRaw],
    layer: 4,
    distilledTokens: nuclearPrefixTokens,
    rawTokens: nuclearRawTokens,
    totalTokens: nuclearPrefixTokens + nuclearRawTokens,
    usable,
    distilledBudget,
    rawBudget,
    refreshLtm: true,
    consecutiveBusts: busts,
    capFit: false,
  };
}

// Public wrapper: records the compressed message count for calibration.
// Calibration needs to know how many messages were SENT to the model (the
// compressed window), not the total DB count. On layer 0 these are equal;
// on layers 1-4 the compressed window is smaller, and the delta on the next
// turn must be computed relative to the compressed count — otherwise the
// expected input on the next turn is anchored to the compressed input token
// count but the "new messages" delta is computed against the full DB count,
// making newMsgCount ≈ 0 and causing layer 0 passthrough on an overflowing session.
export function transform(input: {
  messages: MessageWithParts[];
  projectPath: string;
  sessionID?: string;
  /**
   * Per-request model budget. When provided, applied atomically here so the
   * transform reads THIS request's model caps/pricing — never a concurrently
   * running request's. Omit to use whatever the module globals currently hold
   * (legacy/test path).
   */
  budget?: ModelBudget;
}): TransformResult {
  // Apply the per-request budget synchronously before transformInner so no
  // other request can clobber the globals between here and the transform.
  if (input.budget) applyModelBudget(input.budget);

  // #797: count this transform for the cold-start grace window. Incremented
  // BEFORE transformInner so the very first turn observes transformCount === 1,
  // making the grace cover exactly the first COLD_START_GRACE_TURNS turns of a
  // newly-tracked session. NOT restored from the DB (see makeSessionState /
  // getSessionState), so a resumed/restarted session re-enters the grace —
  // harmless, since it is already calibrated and will not cold-start bust.
  const coldStartSid = input.sessionID ?? input.messages[0]?.info.sessionID;
  if (coldStartSid) getSessionState(coldStartSid).transformCount++;

  const result = transformInner(input);

  // Sanitize non-terminal tool parts before the window reaches the SDK.
  // Must run after transformInner (covers all layers 0-4) and before the
  // trailing-drop loop in index.ts sees the messages.
  result.messages = sanitizeToolParts(result.messages);

  const sid = input.sessionID ?? input.messages[0]?.info.sessionID;
  if (sid) {
    const state = getSessionState(sid);
    state.lastTransformedCount = result.messages.length;
    state.lastTransformEstimate = result.totalTokens;
    state.lastLayer = result.layer;
    // Ratchet the high-water compression layer (1-3 only). Layer 0 never lowers
    // it; layer 4 (emergency) is excluded so a one-shot overflow bust never traps
    // the session at emergency — after recovery it settles back to this floor.
    if (
      result.layer >= 1 &&
      result.layer <= 3 &&
      result.layer > state.maxSustainedLayer
    ) {
      state.maxSustainedLayer = result.layer;
    }
    state.lastWindowMessageIDs = new Set(result.messages.map((m) => m.info.id));

    // Source the shared cache-economics compressed size from the ACTUAL rebuilt
    // window (issue #881), lifted onto the same INPUT scale as cacheSizeFull
    // (issue #886). transformInner seeds cacheSizeCompressed = cacheSizeFull (the
    // no-compaction default) and refined it only inside the layer-0 tier gate;
    // sessions HELD at layer >= 1 by the sticky / prefix-floor / post-idle /
    // first-sight-large guards bypass that gate. We set it authoritatively here
    // from result.layer so EVERY path is correct, and via computeCompressedCacheSize
    // so the compressed size keeps the non-message floor (overhead + LTM, which
    // compaction does NOT remove) and the same UNCALIBRATED_SAFETY factor as full.
    // The full-vs-compressed delta is then the message tokens compaction removed,
    // on the input scale the warmer cross-checks against apiActual — EXACT for
    // uncalibrated turns, APPROXIMATE (conservative, clamp-guarded) for calibrated
    // turns where full is API-measured (see computeCompressedCacheSize). Layer 0 =
    // no compaction (compressed == full).
    state.cacheSizeCompressed = computeCompressedCacheSize(
      result.layer,
      result.totalTokens,
      state.cacheNonBodyTokens,
      state.cacheBodySafety,
      state.cacheSizeFull,
    );
    // Mark wall-clock for onIdleResume() — must record on every transform()
    // so the next-turn idle check has an accurate baseline. Done after the
    // result fields above so a thrown transformInner doesn't update it.
    state.lastTurnAt = Date.now();

    // --- Compaction hint ---
    if (result.layer >= 2) {
      state.consecutiveHighLayer++;
      if (state.consecutiveHighLayer === 3) {
        log.info(
          `session ${sid} has been at gradient layer ${result.layer}+ for 3 consecutive turns.` +
            ` Consider running /compact to reset the context window.`,
        );
      }
    } else {
      state.consecutiveHighLayer = 0;
    }

    // --- Bust-spiral detection (#797) ---
    // Gated by the cold-start grace window (transformCount): in-grace spirals
    // emit an info-level breadcrumb; past-grace spirals fire a high-severity
    // Sentry alert (debounced per episode, cleared on recovery). No-op when no
    // hook is registered (CLI/tests).
    maybeDetectBustSpiral(sid, state, result);

    log.info(
      `gradient: session=${sid} layer=${result.layer} tokens=${result.totalTokens}` +
        ` (distilled=${result.distilledTokens} raw=${result.rawTokens})` +
        ` usable=${result.usable} tier=${getTier(result.totalTokens)} l0cap=${maxLayer0Tokens || "off"}`,
    );
  }
  return result;
}

// Compute our message-only estimate for a set of messages (for calibration use)
export function estimateMessages(messages: MessageWithParts[]): number {
  return messages.reduce((sum, m) => sum + estimateMessage(m), 0);
}

// Identify the current agentic turn: walk backwards from the end to find the
// boundary where it's safe to strip tool outputs. The "current turn" includes:
// 1. All messages from the last user message onwards (the explicit turn boundary)
// 2. All messages that are part of an unfinished tool-call chain BEFORE that user
//    message — because subagent/child user messages can appear mid-chain, and the
//    parent's tool-call chain must be kept intact or the model re-issues tool calls.
//
// The heuristic: walk backwards from the last user message, and if we see assistant
// messages with tool parts (tool-call chains), keep extending the boundary back.
// Stop when we hit a user message that's followed by a non-tool assistant (a clean
// conversational boundary, not a mid-chain subagent injection).
function currentTurnStart(messages: MessageWithParts[]): number {
  if (messages.length === 0) return 0;

  // Start from the last user message
  let boundary = messages.length;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].info.role === "user") {
      boundary = i;
      break;
    }
  }
  if (boundary === messages.length) return 0; // no user message — protect all

  // Now walk backwards past any tool-call chains that precede this user message.
  // A tool-call chain looks like: ...assistant(tool-calls) → user(subagent) → ...
  // We keep extending boundary back while we see tool-bearing assistant messages.
  for (let i = boundary - 1; i >= 0; i--) {
    const msg = messages[i];
    const hasToolParts = msg.parts.some(isToolPart);
    if (hasToolParts) {
      // This assistant message has tools — it's part of an active chain.
      // Extend the boundary to include it.
      boundary = i;
      continue;
    }
    if (msg.info.role === "user") {
      // A user message with no tool-bearing assistant before it — this might be
      // another subagent injection. Keep walking back.
      boundary = i;
      continue;
    }
    // Non-tool assistant message (pure text response) — this is a clean boundary.
    // The chain above this point is a completed conversation turn.
    break;
  }

  return boundary;
}

function tryFit(input: {
  messages: MessageWithParts[];
  prefix: MessageWithParts[];
  prefixTokens: number;
  distilledBudget: number;
  rawBudget: number;
  strip: "none" | "old-tools" | "all-tools";
  protectedTurns?: number;
  /**
   * Optional target budget for the backward fill of OLDER messages. Defaults to
   * `rawBudget`. The current-turn escalation guard always uses the full
   * `rawBudget` (a turn that doesn't fit must still escalate), but the older-
   * message fill can target a smaller budget so the chosen window leaves
   * headroom below the ceiling. Used by tryFitStable's rescan path to evict in
   * a chunk (so the re-pinned boundary doesn't sit right at the ceiling and
   * overflow again next turn).
   */
  rawFillBudget?: number;
}): Omit<
  TransformResult,
  "layer" | "usable" | "distilledBudget" | "rawBudget" | "refreshLtm"
> | null {
  // If distilled prefix exceeds its budget, fail this layer
  if (input.prefixTokens > input.distilledBudget && input.prefix.length > 0)
    return null;

  // Identify the current turn (last user message + all following assistant messages).
  // These are always included — they must never be evicted. If they alone exceed the
  // raw budget, escalate to the next layer (which strips tool outputs to reduce size).
  const turnStart = currentTurnStart(input.messages);
  const currentTurn = input.messages.slice(turnStart);
  const currentTurnTokens = currentTurn.reduce(
    (s, m) => s + estimateMessage(m),
    0,
  );

  if (currentTurnTokens > input.rawBudget) {
    // Current turn alone exceeds budget — can't fit even with everything else dropped.
    // Signal failure so the caller escalates to the next layer (tool-output stripping).
    return null;
  }

  // Walk backwards through older messages (before the current turn),
  // filling the remaining budget after reserving space for the current turn.
  // The fill targets `rawFillBudget` (defaults to rawBudget); callers can pass a
  // smaller value to evict in a chunk and leave headroom below the ceiling. The
  // current turn is always reserved against the full rawBudget (already checked
  // above), so clamp so the older-fill target can't go negative.
  const olderMessages = input.messages.slice(0, turnStart);
  const fillBudget = input.rawFillBudget ?? input.rawBudget;
  const remainingBudget = Math.max(0, fillBudget - currentTurnTokens);
  let olderTokens = 0;
  let cutoff = olderMessages.length; // default: include none of the older messages
  const protectedTurns = input.protectedTurns ?? 0;

  for (let i = olderMessages.length - 1; i >= 0; i--) {
    const msg = olderMessages[i];
    const tokens = estimateMessage(msg);
    if (olderTokens + tokens > remainingBudget) {
      cutoff = i + 1;
      break;
    }
    olderTokens += tokens;
    if (i === 0) cutoff = 0;
  }

  // Ensure role alternation at the prefix/raw boundary: the distilled prefix
  // ends with an assistant message, so the raw window must start with a user.
  // The backward budget scan is purely token-based and can land on any role.
  // If the cutoff produces a raw window starting with assistant(s), advance it
  // past them — otherwise loreMessagesToGateway produces back-to-back assistants
  // and the API rejects with "tool_use ids found without tool_result" (#424).
  if (input.prefix.length > 0) {
    while (
      cutoff < olderMessages.length &&
      olderMessages[cutoff].info.role === "assistant"
    ) {
      olderTokens -= estimateMessage(olderMessages[cutoff]);
      cutoff++;
    }
  }

  // Symmetric guard: a tool_use/tool_result pair is atomic at the gradient
  // boundary. The role-alternation guard above only handles the case where
  // the first kept message is an assistant. The other case — first kept is a
  // user with a tool_result whose issuing assistant was just evicted — is
  // equally broken: it produces an orphan tool_result that the wire format
  // rejects ("tool_use ids were found without tool_result blocks immediately
  // after"). Move cutoff backward to keep the issuing assistant.
  //
  // Note: this may re-include an assistant that the role-alternation guard
  // just skipped, producing back-to-back assistants with the prefix. This
  // is intentional — loreMessagesToGateway coalesces adjacent same-role
  // messages, and if the re-included assistant causes a budget overrun the
  // caller (`tryFitStage` / `transformInner`) re-checks via
  // `fitsWithSafetyMargin()` and escalates to the next layer (which strips
  // tool outputs to make room). The alternative — orphan tool_result — is a
  // hard upstream 400.
  if (
    cutoff > 0 &&
    cutoff < olderMessages.length &&
    olderMessages[cutoff].info.role === "user" &&
    olderMessages[cutoff].parts.some(
      (p) => isToolPart(p) && p.tool === "result",
    ) &&
    olderMessages[cutoff - 1].info.role === "assistant"
  ) {
    olderTokens += estimateMessage(olderMessages[cutoff - 1]);
    cutoff--;
  }

  const rawMessages = [...olderMessages.slice(cutoff), ...currentTurn];
  const rawTokens = olderTokens + currentTurnTokens;

  // Apply system-reminder stripping + optional tool output stripping.
  // The current turn (end of rawMessages) is always "protected" — never stripped.
  const currentTurnSet = new Set(currentTurn.map((m) => m.info.id));
  const processed = rawMessages.map((msg, idx) => {
    const fromEnd = rawMessages.length - idx;
    const isCurrentTurn = currentTurnSet.has(msg.info.id);
    const isProtected =
      isCurrentTurn ||
      input.strip === "none" ||
      (input.strip === "old-tools" && fromEnd <= protectedTurns * 2);
    const parts = isProtected
      ? cleanParts(msg.parts)
      : cleanParts(
          input.strip === "all-tools"
            ? stripToolOutputs(msg.parts)
            : stripToolOutputs(msg.parts),
        );
    const changed = parts !== msg.parts;
    return changed ? { info: msg.info, parts } : msg;
  });

  const total = input.prefixTokens + rawTokens;
  return {
    messages: [...input.prefix, ...processed],
    distilledTokens: input.prefixTokens,
    rawTokens,
    totalTokens: total,
    capFit: false,
  };
}
